// CANONICAL COPY (GH #7115). src/engine/autoEvidence.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/engine/autoEvidence.js
 *
 * Auto-evidence evaluation engine for standard tasks.
 *
 * Given a standard task template and the current tenant state, evaluates
 * whether all (or any) auto-evidence conditions are met.  Returns an
 * evidence snapshot that can be attached to the task instance.
 *
 * This module is pure — no side effects, no state mutations.  It is
 * called by the standard-task recurrence job in the engine, and can also
 * be called from UI components to show live status.
 */

const { MAPPINGS_BY_ID, getEquivalentControls } = require('./controlMappings.cjs');
const { resolveReaders, unresolvedReaderTargets, hasReaderAcknowledged, readerAcknowledgement } = require('./resolveReaders.cjs');
const { withoutSampleRecords } = require('../sampleRecords.cjs');
const { OBLIGATIONS_BY_ID, cadenceToMs } = require('./obligations/index.cjs');
const { requirementRecurs, recurrenceDays } = require('./evidenceRecurrence.cjs');
const { runbookReviewState } = require('./runbookReview.cjs');
const { planWithheldAmong, planWithheldKeyMap, withheldModuleNames, moduleLabelList } = require('./planGateMarker.cjs');
const { trainingModulesOf, activeTrainingModulesOf, trainingProgramsOf, isModuleAssignedToEmployee, currentTrainingEvidence, earliestPastDeadlineForEmployee, deadlinesForEmployee } = require('../trainingDerivations.cjs');

// ── Normalise doc controls (handles legacy string + object formats) ──────

function docControlIds(doc) {
  return (doc.controls || []).map((c) => (typeof c === 'string' ? c : c.controlId));
}

// ── Condition checkers ───────────────────────────────────────────────────
// Each checker returns { pass: boolean, summary: string, details?: object }

function getControlIdsForTask(standardTask) {
  // New path: template has scfId / scfIds
  const rootIds = standardTask.scfIds
    ? standardTask.scfIds
    : standardTask.scfId
      ? [standardTask.scfId]
      : null;

  if (rootIds) {
    const expanded = new Set();
    for (const id of rootIds) {
      for (const equiv of getEquivalentControls(id)) {
        expanded.add(equiv);
      }
    }
    return [...expanded];
  }

  // Backward-compat path: legacy mappingId
  const mapping = MAPPINGS_BY_ID.get(standardTask.mappingId);
  if (!mapping) return [];
  const legacyIds = [
    ...(mapping.iso27002 || []),
    ...(mapping.iso27001 || []),
    ...(mapping.cis18 || []),
  ];
  const expanded = new Set();
  for (const id of legacyIds) {
    for (const equiv of getEquivalentControls(id)) {
      expanded.add(equiv);
    }
  }
  return [...expanded];
}

function docsForControls(documents, controlIds) {
  return documents.filter((d) => {
    const ids = docControlIds(d);
    return controlIds.some((cid) => ids.includes(cid));
  });
}

/**
 * GH #6654, founder decision A — the shared evaluator behind the three JML
 * checklist checkers (`employees-screened`, `employees-nda-signed`,
 * `employees-training`).
 *
 * THE DEFECT IT CLOSES. All three used the JOINER SUBSET as their denominator
 * while their obligations describe the WORKFORCE. A tenant with 200 employees
 * of whom ONE was onboarded through JML with the box ticked returned
 * `pass: true` and the positive summary "All 1 employee(s) have signed NDAs",
 * fulfilling a `strategy: 'any'` obligation while 199 carried no evidence at
 * all. That is not an edge case: it is the ordinary shape of a company that
 * adopted Tryggr with existing staff and has since onboarded a few people.
 *
 * #6507 moved the over-claim threshold from zero joiners to one. This removes
 * it: an employee with no joiner record is UNEVIDENCED, and counts against the
 * verdict exactly like a joiner whose box is unticked.
 *
 * THE DENOMINATOR IS ACTIVE EMPLOYEES, not every row. A departed employee does
 * not need current screening or a current NDA, and counting them would
 * manufacture a permanent failure for any tenant with history — the harm the
 * knowable exception exists to prevent. This matches
 * `evidence-requirement-met`, which has always filtered `status !== 'departed'`.
 *
 * THREE VERDICTS, deliberately distinct:
 *   - no active employees        → `true`. Nothing to evidence, and we know it.
 *   - active employees, NO joiner
 *     records at all             → `null`. Nothing was examined; we cannot
 *                                  determine, rather than having determined a
 *                                  failure (#6507's branch, preserved).
 *   - some joiner records        → `false` when any active employee is
 *                                  unevidenced. We looked, and found gaps.
 * `null` and `false` are both falsy, so neither can fulfil a `strategy: 'any'`
 * obligation; the distinction is what the summary is allowed to claim.
 */
function evaluateJmlChecklistItem(state, { checklistKey, noun, verb }) {
  const all = state.employees || [];
  const employees = all.filter((e) => e.status !== 'departed');

  if (employees.length === 0) {
    return { pass: true, summary: 'No active employees on record (N/A)', details: {} };
  }

  const hasJoiner = (e) => (e.jmlProcesses || []).some((p) => p.type === 'join');
  const withJoin = employees.filter(hasJoiner);
  const withoutJoiner = employees.filter((e) => !hasJoiner(e));

  if (withJoin.length === 0) {
    return {
      pass: null,
      summary: `No joiner records exist for the ${employees.length} active employee(s) on record — ${noun} cannot be evidenced`,
      details: { employeesWithoutJoinerRecord: employees.length, notRecorded: true },
    };
  }

  const unticked = withJoin.filter((e) => {
    const joinProcess = (e.jmlProcesses || []).find((p) => p.type === 'join');
    const item = (joinProcess?.checklist || []).find((c) => c.key === checklistKey);
    return !item || !item.completed;
  });

  const unevidenced = [...withoutJoiner, ...unticked];
  const parts = [];
  if (withoutJoiner.length) parts.push(`${withoutJoiner.length} have no joiner record`);
  if (unticked.length) parts.push(`${unticked.length} have not ${verb}`);

  return {
    pass: unevidenced.length === 0,
    summary:
      unevidenced.length === 0
        ? `All ${employees.length} active employee(s) have ${verb}`
        : `${unevidenced.length} of ${employees.length} active employee(s) cannot evidence ${noun} — ${parts.join(', ')}`,
    details: {
      activeEmployees: employees.length,
      withJoinerRecord: withJoin.length,
      withoutJoinerRecord: withoutJoiner.length,
      untickedCount: unticked.length,
      unevidencedNames: unevidenced.map((e) => e.name || e.id),
    },
  };
}

/**
 * The employees who are leaving or have left.
 *
 * GH #6655 — ONE PREDICATE, because three checkers ask the same question about
 * the same event and each had its own answer. Neither old predicate contained
 * the other, which is the whole defect:
 *
 *   assets-returned-by-leavers      departed OR an ACTIVE leave process
 *     — missed a completed offboarding on somebody never marked departed.
 *   employees-assets-returned       ANY leave process
 *   employees-access-revoked        ANY leave process
 *     — missed somebody marked departed on the employee form and nowhere
 *       else, which is the ordinary path for a tenant that offboards outside
 *       the JML flow. Invisible to access revocation, of all things.
 *
 * So this is the UNION, and every one of the three is now strictly stricter
 * than it was: no population any of them could see is dropped, and each gains
 * the one the others could see.
 *
 * `departed` is the affirmative half — the roster is the tenant's own
 * statement about who is still here, entered once and read everywhere. A leave
 * process is the procedural half, and a COMPLETED one is kept deliberately: a
 * finished offboarding with its checklist ticked is exactly the evidence these
 * checkers exist to find, and an asset still assigned to that person in the
 * registry is exactly the finding they exist to catch.
 */
function leaverPopulation(employees) {
  return (employees || []).filter(
    (e) => e.status === 'departed' || (e.jmlProcesses || []).some((p) => p.type === 'leave')
  );
}

/**
 * The vendors this tenant has declared to be data processors / sub-processors.
 *
 * GH #6655 — ONE PREDICATE, because three checkers ask the same question.
 * `subprocessor-registry-maintained` read `isDataProcessor === true ||
 * category === 'Subprocessor'`; `subprocessor-dpa-signed` and
 * `subprocessor-oversight-current` read only the flag. So a tenant that
 * categorised its vendors as Subprocessor without ticking the checkbox got one
 * criterion evaluating those vendors for real and two asserting nobody is
 * marked a processor — about the same vendors, from the same register.
 * "Enter once, know everywhere": a `has this happened` question must get the
 * same answer whichever surface asks it, and two surfaces that can disagree
 * are the bug.
 */
function processorVendors(vendors) {
  return (vendors || []).filter((v) => v.isDataProcessor === true || v.category === 'Subprocessor');
}

/**
 * The shared verdict the three sub-processor checkers return when the tenant
 * has never declared which of its vendors process personal data on its behalf.
 *
 * GH #6655. `isDataProcessor` is an opt-in checkbox `VendorFormModal` seeds
 * `false` on every new vendor, so "no processors registered" is the DEFAULT
 * state of any tenant that has never classified its register — not a finding
 * that none exist. Returning `pass: true` there made it criterion #1 of a
 * `strategy: 'any'` obligation, which `canAutoComplete` fulfils on
 * `passCount > 0`: the obligation was met by the tenant never answering.
 *
 * Split on knowability, as #6476 established:
 *   no vendors at all      → nothing to classify, and we know it → pass.
 *   vendors, none declared → nobody answered the question → null.
 * Absence of a declaration is not a declaration of absence.
 *
 * READ THIS BEFORE TREATING THE OBLIGATION AS FIXED. All three obligations
 * also carry `document-published` + `document-within-sla`, and under
 * `strategy: 'any'` a published, in-SLA procedure fulfils them on its own —
 * at which point `evaluateAllObligations` rebuilds every criterion of a
 * fulfilled obligation as `pass: true, summary: 'Fulfilled'`, including this
 * one. So on a tenant with an approved procedure and an unclassified register,
 * the customer is still shown "Sub-processor registry is complete — Fulfilled".
 * That is the same shape GH #6608 fixed for `notEntitled` and did not reach
 * here. It is NOT a safety valve and must not be described as one: it is the
 * remaining over-claim, tracked with its blast radius on GH #6698.
 */
function processorsNotDeclared(vendors, detail) {
  if (vendors.length === 0) {
    return { pass: true, summary: 'No vendors on record (N/A)', details: {} };
  }
  return {
    pass: null,
    summary: `None of the ${vendors.length} registered vendor(s) has been marked a data processor on its vendor record — ${detail}`,
    details: { vendors: vendors.length, processors: 0, notRecorded: true },
  };
}

/**
 * The processing register's third-country transfer position.
 *
 * GH #6655 / #6428 — ONE DEFINITION of a documented transfer, shared by
 * `transfer-basis-documented` and `ropa-transfers-documented`, which both
 * answer for SCF-PRV-006 and must never disagree about the same record.
 * Only an Art. 44–49 basis documents a transfer: `ROPAFormModal` also offers
 * 'Other', which is not one. An active activity listing no transfer is the
 * register's default (it never asks for a confirmed "none" — #6658), so it is
 * unanswered, not "no transfers".
 */
const ROPA_VALID_MECHANISMS = [
  'Adequacy decision',
  'Standard Contractual Clauses (SCCs)',
  'Binding Corporate Rules (BCR)',
  'Derogations (Art. 49)',
];
function ropaTransferPosition(state) {
  const active = (state.ropaEntries || []).filter((e) => e.status === 'active');
  const transfers = active.flatMap((e) =>
    (e.thirdCountryTransfers || []).map((t) => ({ entry: e, transfer: t }))
  );
  const undocumented = [
    ...new Set(
      transfers
        .filter(({ transfer }) => !ROPA_VALID_MECHANISMS.includes(transfer.mechanism))
        .map(({ entry }) => entry)
    ),
  ];
  const unanswered = active.filter((e) => (e.thirdCountryTransfers || []).length === 0);
  return { active, transfers, undocumented, unanswered };
}

const CHECKERS = {
  // ── Document-based ─────────────────────────────────────────────────────

  'document-published': (state, standardTask) => {
    const controlIds = getControlIdsForTask(standardTask);
    const docs = docsForControls(state.documents || [], controlIds);
    const published = docs.filter((d) => d.documentStatus === 'published');

    return {
      pass: published.length > 0,
      summary:
        published.length > 0
          ? `${published.length} published document(s): ${published.map((d) => d.title).join(', ')}`
          : 'No published document covers these controls',
      details: { documentIds: published.map((d) => d.id), titles: published.map((d) => d.title) },
    };
  },

  'document-approved': (state, standardTask) => {
    const controlIds = getControlIdsForTask(standardTask);
    const docs = docsForControls(state.documents || [], controlIds);
    const approved = docs.filter((d) => d.approvalDecision === 'approved');

    return {
      pass: approved.length > 0,
      summary:
        approved.length > 0
          ? `${approved.length} approved document(s): ${approved.map((d) => d.title).join(', ')}`
          : 'No approved document covers these controls',
      details: {
        documentIds: approved.map((d) => d.id),
        approvalDates: approved.map((d) => d.approvalDecidedAt),
      },
    };
  },

  // A PUBLISHED DOCUMENT NOBODY IS REQUIRED TO READ IS NOT AN ACKNOWLEDGEMENT
  // (GH #6457, round 4). This checker skipped every document that resolved no
  // readers and then reported "All required readers have acknowledged current
  // document versions" off the documents that remained — which, when NO
  // document resolved a reader, was every one of them. `readerRequirement`
  // defaults to `{ mode: 'none' }` on creation (`DocumentFormModal.jsx`) and the
  // control sits behind "Advanced details", so publishing a policy and leaving
  // that default is the ORDINARY path, not an edge case.
  //
  // That made this the loosest term of `obl-security-awareness`
  // (`strategy: 'any'`, fulfilled on `passCount > 0`): a tenant with zero
  // training modules, zero evidence requirements, zero joiner records and zero
  // acknowledgements by anybody read FULFILLED on the strength of one published
  // document nobody had to read, and Gate 5 passed for SCF-PPL-003. Three prior
  // rounds hardened this obligation's other three terms and moved no tenant's
  // verdict, because this one outvoted them.
  //
  // The mechanism was already written down in this file —
  // `CHECKER_STATE_DEPENDENCIES`'s own note says "an emptied roster resolves no
  // readers, the per-document loop `continue`s on every document, and `allAcked`
  // stays true" — but only for the plan-gating case, so the ordinary-tenant case
  // stayed open.
  //
  // Now: if no document covering these controls resolved a single reader, the
  // claim is not knowable and this returns `pass: null` per §5's fail-safe bias,
  // naming the documents to fix. The knowable exception is kept — with no
  // active employees there is nobody to require, so the vacuous pass is true.
  // That is the same SHAPE as `employees-training`'s and
  // `evidence-requirement-met`'s knowable exception but a NARROWER population:
  // those two exclude `departed` only, this one excludes `offboarding` too,
  // because it has to match `resolveReaders`' own eligibility or the two
  // disagree about whether anybody could have been required. Said explicitly
  // because this comment block's whole subject is citations that were nearly
  // right four rounds running.
  //
  // A mixed set still passes on the documents that DO require readers — the
  // vacuous case is "nobody was required anywhere", not "some document requires
  // nobody" — and the passing summary names the documents that required nobody,
  // because `details` reaches no screen (`obligationEvaluator` builds
  // `fulfilmentState` from the summary alone) and a green tick that silently
  // skipped the awareness policy is the same omission one layer up.
  'document-acknowledged': (state, standardTask) => {
    const controlIds = getControlIdsForTask(standardTask);
    const docs = docsForControls(state.documents || [], controlIds).filter(
      (d) => (d.documentStatus || 'draft') === 'published'
    );
    const employees = state.employees || [];
    // The one list (DOC-B-026): the tenant's not-yet-migrated assignments are
    // folded into each document's readers (design §d.1).
    const documentAssignments = Array.isArray(state.documentAssignments) ? state.documentAssignments : [];

    let allAcked = true;
    const missing = [];
    // DOC-B-026 / design §b: acknowledgements of an earlier version the
    // re-publisher carried forward count, but are evidence of reading THAT
    // version — reported as such, never as a read of the version in force.
    const carriedForward = [];
    // RDRC-04A / design §b: a read an editor recorded on the person's behalf
    // counts — attributed, with the reason given — and is reported as such,
    // never as the person's own read. A legacy acknowledgement naming no
    // recorder counts too and is reported as method unknown.
    const onBehalf = [];
    const methodUnknown = [];
    let docsRequiringReaders = 0;
    const docsRequiringNobody = [];

    // A reader group that resolves to nobody is unmet, as in Gate 3 — the
    // document names readers it cannot reach.
    const deadTargets = [];

    for (const doc of docs) {
      for (const t of unresolvedReaderTargets(doc, employees, documentAssignments)) {
        deadTargets.push({ docId: doc.id, docTitle: doc.title, type: t.type, value: t.value });
      }
      const readers = resolveReaders(doc, employees, documentAssignments);
      if (!readers.length) {
        docsRequiringNobody.push(doc.title || doc.id);
        continue;
      }
      docsRequiringReaders++;
      // ONE QUESTION, ONE ANSWER. `resolveReaders` returns EMPLOYEE ids for
      // every modern `readerRequirement`, and an employee acknowledges through
      // the portal, which writes `employee.documentAcknowledgements` — not
      // `doc.readAcknowledgements`, which only ever holds legacy in-app user
      // acks. This used to compare employee ids against the legacy store
      // directly, so an employee reader could never be seen to have
      // acknowledged anything: before the empty-reader fix above that was
      // masked (a `mode: 'none'` document short-circuited before reaching
      // here), and after it the checker would have told a tenant to set a
      // reader requirement and then refused to clear no matter how many people
      // acknowledged. `hasReaderAcknowledged` resolves the right store per
      // reader and is what Gate 3 already uses on the same documents
      // (`evaluateControl.js`), so the two now agree by construction rather
      // than by coincidence.
      const unread = [];
      for (const r of readers) {
        const ack = readerAcknowledgement(r, doc, employees);
        if (!ack) {
          unread.push(r);
          continue;
        }
        if (ack.method === 'on-behalf') {
          onBehalf.push({
            docId: doc.id,
            docTitle: doc.title,
            readerId: r,
            readVersion: ack.version,
            readAt: ack.at,
            recordedBy: ack.onBehalf.recordedBy,
            recordedByName: ack.onBehalf.recordedByName,
            reason: ack.onBehalf.reason,
            evidenceRef: ack.onBehalf.evidenceRef,
            recordedAt: ack.onBehalf.recordedAt,
          });
        } else if (ack.method === 'unknown') {
          methodUnknown.push({ docId: doc.id, docTitle: doc.title, readerId: r, readVersion: ack.version, readAt: ack.at });
        }
        if (ack.carriedForward.length > 0) {
          carriedForward.push({
            docId: doc.id,
            docTitle: doc.title,
            readerId: r,
            readVersion: ack.version,
            readAt: ack.at,
            currentVersion: doc.version,
            carriedForward: ack.carriedForward.map((c) => ({
              fromVersion: c.fromVersion, toVersion: c.toVersion, by: c.by, at: c.at, reason: c.reason || null,
            })),
          });
        }
      }
      if (unread.length > 0) {
        allAcked = false;
        missing.push({
          docId: doc.id,
          docTitle: doc.title,
          unreadCount: unread.length,
          unreadUsers: unread,
        });
      }
    }

    if (docs.length === 0) {
      return {
        pass: false,
        summary: 'No documents to check acknowledgements against',
        details: {},
      };
    }

    if (deadTargets.length > 0) {
      return {
        pass: false,
        summary: `${deadTargets.length} reader group(s) resolve to nobody, so those documents cannot be read by the people they name: ${deadTargets
          .map((t) => `${t.docTitle || t.docId} (${t.type}: ${t.value})`)
          .join(', ')}`,
        details: { deadTargets, missing, carriedForward, onBehalf, methodUnknown },
      };
    }

    if (docsRequiringReaders === 0) {
      // The population `resolveReaders` itself considers eligible — it excludes
      // `offboarding` as well as `departed`, so this has to match it or the two
      // disagree about whether anybody could have been required.
      const eligible = employees.filter(
        (e) => e.status !== 'departed' && e.status !== 'offboarding'
      );
      if (eligible.length === 0) {
        return {
          pass: true,
          summary: `No active employees, so nobody can be required to acknowledge the ${docs.length} published document(s) covering these controls (N/A)`,
          details: { documentsRequiringNobody: docsRequiringNobody },
        };
      }
      return {
        pass: null,
        summary: `No published document covering these controls requires anybody to read it, so acknowledgement cannot be evidenced — set a reader requirement on: ${docsRequiringNobody.join(', ')}`,
        details: {
          activeEmployees: eligible.length,
          documentsRequiringNobody: docsRequiringNobody,
        },
      };
    }

    return {
      pass: allAcked,
      summary: allAcked
        ? (carriedForward.length
            ? `All required readers of the ${docsRequiringReaders} document(s) that require one have acknowledged — ${carriedForward.length} of those acknowledgements ${carriedForward.length === 1 ? 'is a read of an earlier version' : 'are reads of an earlier version'}, carried forward to the current one by the person who re-published it (see details)`
            : `All required readers have acknowledged current versions of the ${docsRequiringReaders} document(s) that require one`) +
          (onBehalf.length
            ? ` — ${onBehalf.length} ${onBehalf.length === 1 ? 'acknowledgement was' : 'acknowledgements were'} recorded on the reader's behalf by an editor (see details)`
            : '') +
          (methodUnknown.length
            ? ` — ${methodUnknown.length} ${methodUnknown.length === 1 ? 'acknowledgement is a legacy record' : 'acknowledgements are legacy records'} that do not say who recorded ${methodUnknown.length === 1 ? 'it' : 'them'} (method unknown)`
            : '') +
          (docsRequiringNobody.length
            ? ` — ${docsRequiringNobody.length} further ${docsRequiringNobody.length === 1 ? 'document requires' : 'documents require'} nobody to read ${docsRequiringNobody.length === 1 ? 'it' : 'them'} and ${docsRequiringNobody.length === 1 ? 'was' : 'were'} not assessed: ${docsRequiringNobody.join(', ')}`
            : '')
        : `${missing.length} document(s) have unacknowledged readers`,
      details: {
        missing,
        carriedForward,
        onBehalf,
        methodUnknown,
        documentsRequiringReaders: docsRequiringReaders,
        documentsRequiringNobody: docsRequiringNobody,
      },
    };
  },

  'document-within-sla': (state, standardTask) => {
    const controlIds = getControlIdsForTask(standardTask);
    const docs = docsForControls(state.documents || [], controlIds).filter(
      (d) => (d.documentStatus || 'draft') === 'published'
    );
    const now = Date.now();

    // #7146: a published document with no nextReview has no review schedule,
    // so it can never show it was reviewed on time — it is not within SLA.
    const undated = docs.filter((d) => !d.nextReview);
    const overdue = docs.filter((d) => d.nextReview && new Date(d.nextReview).getTime() <= now);

    if (docs.length === 0) {
      return { pass: false, summary: 'No documents to check SLA against', details: {} };
    }

    const problems = [];
    if (overdue.length)
      problems.push(
        `${overdue.length} document(s) overdue for review: ${overdue.map((d) => d.title).join(', ')}`
      );
    if (undated.length)
      problems.push(
        `${undated.length} document(s) have no review date: ${undated.map((d) => d.title).join(', ')}`
      );

    return {
      pass: problems.length === 0,
      summary: problems.length === 0 ? 'All documents reviewed within SLA' : problems.join('; '),
      details: {
        overdueDocIds: overdue.map((d) => d.id),
        overdueTitles: overdue.map((d) => d.title),
        undatedDocIds: undated.map((d) => d.id),
        undatedTitles: undated.map((d) => d.title),
      },
    };
  },

  // ISO 22301 Cl. 8.5 — business continuity exercises. #4847: logging a
  // completed DR test in the BCDR module previously had no effect on this
  // obligation at all — no checker anywhere read state.bcdrDrTests, so the
  // obligation could only ever be evidenced by a published exercise-report
  // document, never by the exercise actually having taken place. Passes when
  // EITHER a published, in-SLA document covers the relevant controls (the
  // existing evidence path — unchanged, so any tenant currently passing via a
  // report keeps passing) OR at least one DR test/exercise was logged with
  // status 'completed' and a conductedDate within the obligation's own 1-year
  // cadence window (this obligation's cadence.frequency) — a real exercise
  // having happened is at least as strong evidence as a document describing
  // one. This is an additive OR, never a replacement for the document path.
  'continuity-exercise-evidence': (state, standardTask) => {
    const controlIds = getControlIdsForTask(standardTask);
    const docs = docsForControls(state.documents || [], controlIds).filter(
      (d) => (d.documentStatus || 'draft') === 'published'
    );
    const now = Date.now();
    // #7146: a report with no nextReview has no review schedule, so it is not current.
    const currentDoc = docs.find((d) => d.nextReview && new Date(d.nextReview).getTime() > now);

    const CADENCE_MS = 365 * 24 * 60 * 60 * 1000; // matches this obligation's cadence.frequency ('1y')
    const drTests = state.bcdrDrTests || [];
    // ISO 22301 Cl. 8.5 asks the exercise to VALIDATE that recovery objectives
    // can be met. A test recorded as 'fail' or 'partial' — or completed and
    // never assessed — demonstrates the opposite, or nothing; counting it as
    // evidence would advance this obligation on the strength of a failure.
    // Only an exercise the tenant itself marked 'pass' evidences capability.
    const passedTests = drTests.filter(
      (t) => t.status === 'completed' && t.conductedDate && t.outcome === 'pass'
    );
    const recentTest = passedTests
      .filter((t) => now - new Date(t.conductedDate).getTime() <= CADENCE_MS)
      .sort((a, b) => new Date(b.conductedDate).getTime() - new Date(a.conductedDate).getTime())[0];

    const pass = !!currentDoc || !!recentTest;
    const summaryParts = [];
    if (currentDoc) summaryParts.push(`published, current exercise report ("${currentDoc.title}")`);
    if (recentTest)
      summaryParts.push(
        `a passed DR test/exercise conducted ${recentTest.conductedDate} ("${recentTest.title}")`
      );

    return {
      pass,
      summary: pass
        ? `Evidenced by ${summaryParts.join(' and ')}`
        : 'No published, current exercise report and no passed DR test/exercise within the last year',
      details: {
        documentId: currentDoc?.id || null,
        drTestId: recentTest?.id || null,
        drTestConductedDate: recentTest?.conductedDate || null,
      },
    };
  },

  // SCF-BCM-002 — "ICT readiness shall be planned, implemented, MAINTAINED and
  // tested". An active runbook asserts that a system can be recovered by
  // following it; this criterion is what makes that assertion expire. It fails
  // when an active runbook's review is overdue, when the runbook has been
  // edited since its last review (the review no longer covers what the runbook
  // says), or when the runbook carries no date of any kind to reckon from.
  //
  // At ZERO active runbooks it passes, and says so with its denominator. That
  // is not the vacuous pass SDEB-01 closed: this criterion only ever subtracts
  // from an obligation whose evidence is carried by continuity-exercise-
  // evidence above. A tenant with a passed DR test and no runbooks HAS
  // exercised its continuity arrangements, which is what obl-bc-exercise
  // asserts — failing it there would demand runbooks the obligation's own
  // scope never required, and manufacture evidence-shaped records to clear it.
  // Whether a tenant should hold runbooks at all is the readiness pillar's
  // question, and it already withholds its points at zero.
  //
  // Reads documents and DR tests as well as runbooks, because the due date
  // resolves across all three (see src/utils/runbookReview.js) —
  // CHECKER_STATE_DEPENDENCIES declares each of them so a plan withholding one
  // cannot silently change which date governs.
  'runbook-review-current': (state) => {
    const active = (state.bcdrRunbooks || []).filter((r) => r && r.status === 'active');
    if (!active.length) {
      return {
        pass: true,
        summary: 'No active ICT recovery runbooks — review currency not assessed (0 of 0)',
        details: { assessed: 0, current: 0, overdue: [], stale: [], unanchored: [] },
      };
    }

    const documents = state.documents || [];
    // A linked exercise that did not pass brings a runbook's review forward
    // (src/utils/runbookReview.js). A passed one deliberately changes nothing
    // here: this criterion and continuity-exercise-evidence above test
    // different propositions, and one record must not satisfy both.
    const drTests = state.bcdrDrTests || [];
    const overdue = [];
    const stale = [];
    const unanchored = [];
    let currentCount = 0;

    for (const runbook of active) {
      const review = runbookReviewState(runbook, documents, drTests);
      if (review.current) {
        currentCount += 1;
        continue;
      }
      if (review.unanchored) unanchored.push(runbook.id);
      else if (review.overdue) overdue.push(runbook.id);
      if (review.stale) stale.push(runbook.id);
    }

    const pass = currentCount === active.length;
    const reasons = [];
    if (overdue.length) reasons.push(`${overdue.length} overdue for review`);
    if (stale.length) reasons.push(`${stale.length} edited since the last review`);
    if (unanchored.length) reasons.push(`${unanchored.length} with no review date to reckon from`);

    return {
      pass,
      summary: pass
        ? `All ${active.length} active runbooks carry a current review`
        : `${currentCount} of ${active.length} active runbooks carry a current review — ${reasons.join(', ')}`,
      details: { assessed: active.length, current: currentCount, overdue, stale, unanchored },
    };
  },

  // ISO 27001 Cl. 9.2 / SCF-AUD-001 — internal ISMS audit programme.
  // Passes when the tenant has actually run internal audits, not only when a
  // programme/report document has been uploaded. Two independent signals,
  // either is sufficient: (1) an audit programme (state.auditProgrammes) with
  // status 'complete', or (2) a completed internal audit (state.internalAudits)
  // with an auditDate inside the obligation's cadence window (matches
  // continuity-exercise-evidence's pattern above) — covering audits run outside
  // a formal programme. This is additive: the document-published /
  // manual-attestation criteria on obl-internal-audit are unchanged, so a
  // tenant that prefers to evidence this via an uploaded report keeps passing.
  //
  // TWO THINGS THIS USED TO GET WRONG, both of which reported clause 9.2 as met
  // when it was not:
  //
  // (a) Signal (1) was UNBOUNDED BY DATE. A programme marked complete in 2023
  //     satisfied a one-year cadence today, and forever. A signal carrying no
  //     date can never satisfy an obligation carrying a cadence, so the
  //     programme's date is now established from the audits it actually
  //     contains, and a programme from which no date can be established does
  //     not pass on its own.
  // (b) This comment used to credit signal (1) as "real, already-verified
  //     evidence" because AuditProgrammeTab.updateAuditStatus() sets a
  //     programme complete only once every item is complete. That was wrong
  //     twice over: AuditProgrammeTab is imported by nothing (ProgrammeWorkspace
  //     is the live surface and never writes prog.status), so no live path
  //     produces this signal at all; and item completion was itself a button
  //     click that verified nothing. Only legacy rows carry it.
  //
  // What a completed AUDIT owes before it may carry this signal is now gated at
  // the transition (FIND-B-008, src/utils/auditCompletion.js and its server
  // copy). Records completed before that gate existed are not re-derived here:
  // a conclusion and a named lead auditor are asked for, because they are facts
  // the record already has fields for and an auditor asks for by name, but
  // scope coverage is not, because an audit legitimately run and documented
  // outside the product has no in-product assessment and must not flip red.
  'internal-audit-programme-complete': (state) => {
    const CADENCE_MS = 365 * 24 * 60 * 60 * 1000; // matches obl-internal-audit cadence.frequency ('1y')
    const now = Date.now();

    const audits = state.internalAudits || [];
    // Bounded on BOTH sides. `now - t <= CADENCE_MS` alone is negative for a
    // future date and therefore passes it, so a mistyped or deliberately
    // forward-dated audit would evidence clause 9.2 on a day that has not
    // happened yet. An audit that has not happened evidences nothing.
    const withinCadence = (iso) => {
      const t = iso ? new Date(iso).getTime() : NaN;
      return Number.isFinite(t) && t <= now && now - t <= CADENCE_MS;
    };
    // An audit only carries this signal if it says who reached what verdict.
    // Both are fields the record has always had, and both are what an external
    // auditor asks for by name; a record with neither evidences nothing.
    const substantiated = (a) =>
      a.status === 'complete' && !!a.leadAuditorId && !!String(a.conclusion || '').trim();

    const programmes = state.auditProgrammes || [];
    // A programme's own date, taken from the audits it contains — the record
    // itself carries none. There is deliberately NO fallback to the `year`
    // label: a year is not a date on which anything happened, and synthesising
    // one from it credits a programme containing no audits at all. Reading it
    // as that year's last day (the first attempt at this) is the most generous
    // reading available, which is the wrong direction — a synthesised date
    // must be the earliest defensible instant or none, never the latest. So a
    // programme whose audits establish no date does not carry this signal.
    const programmeDate = (p) => {
      const dates = audits
        .filter((a) => a.programId === p.id && a.auditDate)
        .map((a) => a.auditDate)
        .sort();
      return dates.length ? dates[dates.length - 1] : null;
    };
    const completeProgramme = programmes
      .filter((p) => p.status === 'complete')
      .find((p) => withinCadence(programmeDate(p)));

    const recentCompleteAudit = audits
      .filter((a) => substantiated(a) && withinCadence(a.auditDate))
      .sort((a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime())[0];

    const pass = !!completeProgramme || !!recentCompleteAudit;
    const summaryParts = [];
    if (completeProgramme)
      summaryParts.push(
        `completed audit programme "${completeProgramme.name || completeProgramme.id}"`
      );
    if (recentCompleteAudit)
      summaryParts.push(
        `a completed internal audit conducted ${recentCompleteAudit.auditDate} ("${recentCompleteAudit.name || recentCompleteAudit.ref || recentCompleteAudit.id}")`
      );

    return {
      pass,
      summary: pass
        ? `Evidenced by ${summaryParts.join(' and ')}`
        : 'No completed audit programme and no completed internal audit within the last year',
      details: {
        programmeId: completeProgramme?.id || null,
        internalAuditId: recentCompleteAudit?.id || null,
        internalAuditDate: recentCompleteAudit?.auditDate || null,
      },
    };
  },

  // ── Risk-based ─────────────────────────────────────────────────────────

  'risk-register-exists': (state) => {
    const risks = state.risks || [];
    return {
      pass: risks.length > 0,
      summary:
        risks.length > 0
          ? `Risk register contains ${risks.length} risk(s)`
          : 'Risk register is empty',
      details: { riskCount: risks.length },
    };
  },

  'risks-reviewed': (state) => {
    const risks = state.risks || [];
    if (risks.length === 0) {
      return { pass: false, summary: 'No risks in register to review', details: {} };
    }
    const unreviewed = risks.filter((r) => r.status === 'open');
    return {
      pass: unreviewed.length === 0,
      summary:
        unreviewed.length === 0
          ? `All ${risks.length} risk(s) have been reviewed`
          : `${unreviewed.length} risk(s) still in initial "open" state`,
      details: { unreviewedCount: unreviewed.length, unreviewedIds: unreviewed.map((r) => r.id) },
    };
  },

  'risks-approved': (state) => {
    const risks = state.risks || [];
    if (risks.length === 0) {
      return { pass: false, summary: 'No risks in register', details: {} };
    }
    const withApproval = risks.filter(
      (r) =>
        r.status === 'approved' ||
        r.status === 'resolved' ||
        r.status === 'accepted' ||
        r.status === 'transferred'
    );
    return {
      pass: withApproval.length > 0,
      summary:
        withApproval.length > 0
          ? `${withApproval.length} risk(s) have been approved/resolved`
          : 'No risks have approved resolutions',
      details: { approvedCount: withApproval.length },
    };
  },

  // ── Vendor-based ───────────────────────────────────────────────────────

  // Fails closed (not N/A) when the vendor registry is empty — a tenant
  // with zero vendors registered cannot be evidenced as having assessed
  // its vendors (SDEB-04).
  'vendor-assessed': (state) => {
    const vendors = state.vendors || [];
    if (vendors.length === 0) {
      return {
        pass: false,
        summary: 'No vendors registered — vendor assessments cannot be evidenced',
        details: {},
      };
    }
    const assessed = vendors.filter(
      (v) => v.assessmentAnswers && Object.keys(v.assessmentAnswers).length > 0
    );
    return {
      pass: assessed.length === vendors.length,
      summary:
        assessed.length === vendors.length
          ? `All ${vendors.length} vendor(s) have completed assessments`
          : `${assessed.length}/${vendors.length} vendors assessed`,
      details: {
        total: vendors.length,
        assessed: assessed.length,
        unassessed: vendors
          .filter((v) => !v.assessmentAnswers || Object.keys(v.assessmentAnswers).length === 0)
          .map((v) => v.name || v.id),
      },
    };
  },

  // Fails closed (not N/A) when the vendor registry is empty — see
  // 'vendor-assessed' above (SDEB-04).
  'vendor-assessment-current': (state) => {
    const vendors = state.vendors || [];
    if (vendors.length === 0) {
      return {
        pass: false,
        summary: 'No vendors registered — vendor assessment currency cannot be evidenced',
        details: {},
      };
    }
    const now = Date.now();
    const overdue = vendors.filter(
      (v) => v.nextAssessmentDate && new Date(v.nextAssessmentDate).getTime() <= now
    );
    return {
      pass: overdue.length === 0,
      summary:
        overdue.length === 0
          ? 'All vendor assessments are current'
          : `${overdue.length} vendor(s) have overdue assessments`,
      details: { overdueVendors: overdue.map((v) => v.name || v.id) },
    };
  },

  // Fails closed (not N/A) when the vendor registry is empty — see
  // 'vendor-assessed' above (SDEB-04).
  'vendor-contracts-tracked': (state) => {
    const vendors = state.vendors || [];
    if (vendors.length === 0) {
      return {
        pass: false,
        summary: 'No vendors registered — vendor contract tracking cannot be evidenced',
        details: {},
      };
    }
    const tracked = vendors.filter((v) => v.contractEnd);
    return {
      pass: tracked.length === vendors.length,
      summary:
        tracked.length === vendors.length
          ? `All ${vendors.length} vendor contract(s) have end dates tracked`
          : `${tracked.length}/${vendors.length} vendors have contract end dates`,
      details: {
        total: vendors.length,
        tracked: tracked.length,
        untracked: vendors.filter((v) => !v.contractEnd).map((v) => v.name || v.id),
      },
    };
  },

  // GDPR Art. 28(2); FSR ISAE 3000 F.1, F.5 — sub-processor registry has the
  // minimum identifying information for every processor vendor and every
  // declared sub-processor.
  'subprocessor-registry-maintained': (state) => {
    const vendors = state.vendors || [];
    const processors = processorVendors(vendors);
    if (processors.length === 0) {
      return processorsNotDeclared(vendors, 'a sub-processor registry cannot be evidenced');
    }
    const incomplete = processors.filter((v) => {
      if (!v.name) return true;
      const hasIdentifier = !!(v.registryId || v.companyType);
      if (!hasIdentifier) return true;
      if (!v.address) return true;
      if (!v.processingPurpose) return true;
      const subProcs = v.subProcessors || [];
      const subIncomplete = subProcs.some((sp) => !sp.name || !sp.country);
      return subIncomplete;
    });
    return {
      pass: incomplete.length === 0,
      summary:
        incomplete.length === 0
          ? `Sub-processor registry complete: ${processors.length} processor vendor(s) with name, registration ID, address and processing purpose recorded`
          : `${incomplete.length} processor vendor(s) missing required registry fields: ${incomplete.map((v) => v.name || v.id).join(', ')}`,
      details: {
        total: processors.length,
        incompleteCount: incomplete.length,
        incompleteNames: incomplete.map((v) => v.name || v.id),
      },
    };
  },

  // GDPR Art. 28(3); FSR ISAE 3000 F.2, F.4 — every processor vendor has a
  // signed, in-date DPA. dpaExpiry empty is treated as no expiry.
  'subprocessor-dpa-signed': (state) => {
    const vendors = state.vendors || [];
    const processors = processorVendors(vendors);
    if (processors.length === 0) {
      return processorsNotDeclared(vendors, 'signed processor agreements cannot be evidenced');
    }
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const unsigned = [];
    const expired = [];
    const draft = [];
    for (const v of processors) {
      if (v.dpaStatus === 'expired') {
        expired.push(v);
        continue;
      }
      if (v.dpaStatus === 'draft' || v.dpaStatus === 'none' || !v.dpaStatus) {
        draft.push(v);
        continue;
      }
      if (v.dpaStatus === 'signed') {
        if (!v.dpaSigned) {
          unsigned.push(v);
          continue;
        }
        if (v.dpaExpiry && new Date(v.dpaExpiry).getTime() <= today.getTime()) {
          expired.push(v);
          continue;
        }
      } else {
        unsigned.push(v);
      }
    }
    const failing = [...unsigned, ...expired, ...draft];
    return {
      pass: failing.length === 0,
      summary:
        failing.length === 0
          ? `All ${processors.length} processor vendor(s) have signed, in-date DPAs`
          : `${failing.length} processor vendor(s) without signed/in-date DPA: ${failing.map((v) => v.name || v.id).join(', ')}`,
      details: {
        total: processors.length,
        unsigned: unsigned.map((v) => v.name || v.id),
        expired: expired.map((v) => v.name || v.id),
        draft: draft.map((v) => v.name || v.id),
      },
    };
  },

  // GDPR Art. 28(2), Art. 28(4); FSR ISAE 3000 F.3, F.6 — processor vendors
  // are assessed within the last 12 months and have a future next-assessment
  // date on file.
  'subprocessor-oversight-current': (state) => {
    const vendors = state.vendors || [];
    const processors = processorVendors(vendors);
    if (processors.length === 0) {
      return processorsNotDeclared(vendors, 'processor oversight cannot be evidenced');
    }
    const now = new Date();
    const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;
    const stale = processors.filter((v) => {
      const lastOk =
        v.lastAssessmentDate &&
        now.getTime() - new Date(v.lastAssessmentDate).getTime() <= TWELVE_MONTHS_MS;
      const nextOk =
        v.nextAssessmentDate && new Date(v.nextAssessmentDate).getTime() > now.getTime();
      return !(lastOk && nextOk);
    });
    return {
      pass: stale.length === 0,
      summary:
        stale.length === 0
          ? `All ${processors.length} processor vendor(s) assessed within the last 12 months with assessments current`
          : `${stale.length} processor vendor(s) with stale or overdue oversight: ${stale.map((v) => v.name || v.id).join(', ')}`,
      details: {
        total: processors.length,
        staleCount: stale.length,
        staleNames: stale.map((v) => v.name || v.id),
      },
    };
  },

  // GDPR Art. 44–49; FSR ISAE 3000 G.1–G.3 — every third-country transfer the
  // tenant has recorded carries a documented transfer mechanism.
  //
  // GH #6655. This is criterion #1 of `obl-transfer-basis-documentation`,
  // `strategy: 'any'`, so a pass here fulfils the obligation on its own — and
  // it used to pass whenever no vendor listed transfer countries. That is the
  // DEFAULT state of every vendor record: `VendorFormModal` seeds
  // `transferMechanism: ''`, which until this fix was the option labelled
  // "N/A — No third-country transfer", so a record nobody touched read exactly
  // like an affirmed "none". Absence of a declaration is not a declaration of
  // absence.
  //
  // So a vendor's transfer position is ANSWERED only when it either declares a
  // transfer (countries or a mechanism) or carries the explicit `none` answer
  // the form now offers. Legacy `''` keeps meaning "not answered" — no tenant
  // data is rewritten. Transfers are read from BOTH records the product keeps
  // (enter once, know everywhere): the vendor register and the processing
  // register's `thirdCountryTransfers`, so the two can never disagree about
  // whether a transfer is documented.
  //
  //   any recorded transfer without a valid mechanism → false
  //   any vendor or active processing activity whose
  //   transfer position is unanswered                 → null, naming them
  //   nothing recorded in either register             → null
  //   otherwise                                       → pass
  'transfer-basis-documented': (state) => {
    const vendors = state.vendors || [];
    const VALID_MECHANISMS = ['adequacy', 'scc', 'bcr', 'derogation'];
    const name = (x) => x.name || x.id;
    const listsCountries = (v) =>
      typeof v.transferCountries === 'string' && v.transferCountries.trim() !== '';
    const declaresTransfer = (v) =>
      listsCountries(v) || VALID_MECHANISMS.includes(v.transferMechanism);
    const vendorTransfers = vendors.filter(declaresTransfer);
    const unanswered = vendors.filter((v) => !declaresTransfer(v) && v.transferMechanism !== 'none');
    // A vendor answered `none` that also lists countries contradicts itself;
    // `none` is not a mechanism, so it is undocumented.
    const undocumentedVendors = vendorTransfers.filter(
      (v) => !VALID_MECHANISMS.includes(v.transferMechanism)
    );
    const {
      transfers: ropaTransfers,
      undocumented: undocumentedRopa,
      unanswered: unansweredRopa,
    } = ropaTransferPosition(state);
    const details = {
      vendors: vendors.length,
      vendorTransfers: vendorTransfers.length,
      ropaTransfers: ropaTransfers.length,
      undocumentedNames: undocumentedVendors.map(name),
      undocumentedRopaNames: undocumentedRopa.map(name),
      unansweredNames: unanswered.map(name),
      unansweredRopaNames: unansweredRopa.map(name),
    };

    if (undocumentedVendors.length + undocumentedRopa.length > 0) {
      const parts = [];
      if (undocumentedVendors.length > 0)
        parts.push(
          `${undocumentedVendors.length} vendor(s) with undeclared transfer mechanism: ${undocumentedVendors.map(name).join(', ')}`
        );
      if (undocumentedRopa.length > 0)
        parts.push(
          `${undocumentedRopa.length} processing activit${undocumentedRopa.length === 1 ? 'y' : 'ies'} with an undocumented transfer: ${undocumentedRopa.map(name).join(', ')}`
        );
      return { pass: false, summary: parts.join('; '), details };
    }
    if (unanswered.length > 0) {
      return {
        pass: null,
        summary: `${unanswered.length} vendor(s) have no recorded third-country transfer position — set a transfer mechanism, or confirm "No third-country transfer", on: ${unanswered.map(name).join(', ')}`,
        details: { ...details, notRecorded: true },
      };
    }
    if (unansweredRopa.length > 0) {
      return {
        pass: null,
        summary: `${unansweredRopa.length} active processing activit${unansweredRopa.length === 1 ? 'y records' : 'ies record'} no international transfer, but the register never asks the question — so no transfer position is evidenced: ${unansweredRopa.map(name).join(', ')}`,
        details: { ...details, notRecorded: true },
      };
    }
    if (vendors.length === 0 && ropaTransfers.length === 0) {
      return {
        pass: null,
        summary:
          'No vendors and no recorded transfers — whether any third-country transfers exist has not been recorded, so transfer bases cannot be evidenced',
        details: { ...details, notRecorded: true },
      };
    }
    const total = vendorTransfers.length + ropaTransfers.length;
    return {
      pass: true,
      summary:
        total === 0
          ? `All ${vendors.length} vendor(s) confirmed as making no third-country transfer`
          : `All ${total} recorded third-country transfer(s) have a documented Art. 44–49 mechanism`,
      details,
    };
  },

  // ── Employee / JML-based ───────────────────────────────────────────────

  // GH #6507 — the same narrowing as `employees-training` below, for the same
  // reason. This is the ONLY non-`evidence-requirement-met` term of
  // obl-employee-screening, which is `strategy: 'any'`, so a vacuous `true`
  // here fulfils that obligation on its own no matter what the two
  // `evidence-requirement-met` terms observe — and both of those were hardened
  // under #6457, which is precisely what leaves this one load-bearing.
  //
  // "No employee carries a `join` JML process" is the DEFAULT state of any
  // tenant that has not run the joiner flow, so pre-employment screening read
  // as satisfied on tenants where nothing had been screened and nothing had
  // been looked at (TRYGGR_INTENT.md §6.2).
  //
  // The empty population splits on whether the claim is KNOWABLE:
  //   - no employees at all   → nobody to screen; vacuously true, and true.
  //   - employees, none with
  //     a joiner record       → the JML checklist that would carry this answer
  //                             was never created. Absence of evidence, not
  //                             evidence of screening → `pass: null` per §5.
  // `null` is falsy in every consumer, so this fails safe with no call-site
  // change.
  //
  // CLOSED by GH #6654 (founder decision A). The denominator was the joiner
  // subset while the obligation describes the workforce, so one onboarded
  // employee fulfilled it for a workforce of any size. All three checkers now
  // share `evaluateJmlChecklistItem`, which counts an employee with no joiner
  // record as unevidenced and names the real denominator.
  'employees-screened': (state) =>
    evaluateJmlChecklistItem(state, {
      checklistKey: 'screening-complete',
      noun: 'pre-employment screening',
      verb: 'completed screening',
    }),

  // GH #6507 — same narrowing, same reason. This is the ONLY
  // non-`evidence-requirement-met` term of obl-nda-signed (`strategy: 'any'`),
  // whose other term was hardened under #6457 — so this one alone decided the
  // obligation on any tenant without joiner records, and said "signed" having
  // examined nothing.
  //
  // Partial-population gap closed by GH #6654 — see `evaluateJmlChecklistItem`.
  'employees-nda-signed': (state) =>
    evaluateJmlChecklistItem(state, {
      checklistKey: 'nda-signed',
      noun: 'a signed NDA',
      verb: 'signed an NDA',
    }),

  // A DISJUNCTION IS ONLY AS STRICT AS ITS LOOSEST TERM (GH #6457).
  // This is a fulfilment criterion of `obl-security-awareness`, which is
  // `strategy: 'any'` — so whatever this returns can satisfy that whole
  // obligation on its own, exactly as `training-completion-recorded` could
  // before it was hardened. Hardening one criterion of an `any` obligation and
  // leaving its sibling returning a vacuous `true` on the same empty population
  // moves no tenant's verdict; it only moves where the over-claim comes from.
  //
  // The empty population is split, on whether the claim is KNOWABLE:
  //   - no employees at all      → nothing to train; vacuously true, and true.
  //   - employees, none with a
  //     joiner record            → the JML checklist that would carry this
  //                                answer was never created. That is the
  //                                absence of evidence, not evidence of
  //                                training, so `pass: null` per §5's fail-safe
  //                                bias — the same narrowing applied to
  //                                training-deadline-overdue's archive branch.
  // `null` is falsy in every consumer, so this fails safe with no call-site
  // change; `std-security-awareness` reads the same checker under
  // `strategy: 'all'`, where it is likewise stricter and never looser.
  'employees-training': (state) =>
    evaluateJmlChecklistItem(state, {
      checklistKey: 'security-training',
      noun: 'JML security training',
      verb: 'completed security training',
    }),

  // GH #6655 — resolves leavers through the shared `leaverPopulation`, so a
  // `departed` employee with no leaver record is counted here too. While this
  // filtered on "has a leave process" alone, offboarding somebody on the
  // employee form and nowhere else made them invisible to the one criterion
  // that asks whether their access was taken away.
  //
  // Empty population still fails closed rather than returning the unknown
  // verdict its asset-return sibling uses: this checker only ever runs under
  // `strategy: 'all'` (obl-offboarding-access, obl-access-rights-review), where
  // a `false` is the strict answer and siblings carry the other routes.
  'employees-access-revoked': (state) => {
    const employees = state.employees || [];
    const leavers = leaverPopulation(employees);
    if (leavers.length === 0) {
      return {
        pass: false,
        summary: 'No leaver records exist yet — access revocation cannot be evidenced',
        details: {},
      };
    }
    const unrevoked = leavers.filter((e) => {
      // RDRC-04 (founder decision A, 2026-10-19): entra-sync stamps
      // entraEnabledWhileDepartedAt while the customer's identity provider
      // reports a departed employee's account enabled. A ticked checklist item
      // records that somebody revoked access once; the sync's observation says
      // the account is live now, and the observation wins (TRYGGR_INTENT §6.2).
      if (e.entraEnabledWhileDepartedAt) return true;
      const leaveProcess = (e.jmlProcesses || []).find((p) => p.type === 'leave');
      if (!leaveProcess) return true; // departed, no leaver record — nothing evidences revocation
      const item = (leaveProcess.checklist || []).find((c) => c.key === 'access-revoked');
      return !item || !item.completed;
    });
    const stillEnabled = unrevoked.filter((e) => e.entraEnabledWhileDepartedAt);
    return {
      pass: unrevoked.length === 0,
      summary:
        unrevoked.length === 0
          ? `All ${leavers.length} leaver(s)/departed employee(s) have had access revoked`
          : `${unrevoked.length} of ${leavers.length} leaver(s)/departed employee(s) cannot evidence access revocation` +
            (stillEnabled.length ? ` — ${stillEnabled.length} still enabled in Entra ID` : ''),
      details: {
        leaverCount: leavers.length,
        unrevokedNames: unrevoked.map((e) => e.name || e.id),
        enabledInIdentityProviderNames: stillEnabled.map((e) => e.name || e.id),
      },
    };
  },

  // GH #6655 — the LOOSEST TERM PROBLEM, in the leaver half of the family.
  //
  // `obl-asset-return` is `strategy: 'any'` over this checker and
  // `assets-returned-by-leavers`, and the two resolved DIFFERENT leaver
  // populations (see `leaverPopulation` above for both, and the union that
  // replaced them). So a tenant that marked someone `departed` on the employee
  // form without running the leaver flow, while that person still held a
  // registry laptop, got a real `false` from the registry term and a vacuous
  // `true` from this one — and `any` takes the loosest. The product reported
  // SCF-AST-003 fulfilled while a departed employee held company equipment.
  // The vacuous pass did not merely fail to add information: it OUTVOTED an
  // observed failure, which is worse than the shape #6507 fixed, where nothing
  // else had looked at all.
  //
  // Both terms now resolve the same population, so neither can outvote the
  // other on employees the other cannot see. They remain genuinely different
  // EVIDENCE for the same fact — a clear asset registry, or a completed
  // offboarding checklist — which is what makes `any` legitimate here rather
  // than a loophole. A leaver with no leaver record at all evidences neither,
  // so this checker counts them as unevidenced rather than as nothing to check.
  //
  // NOTE, because the usual safety argument does NOT apply here:
  // `obl-asset-return` carries no `document-*` criterion, so unlike #6428 and
  // #6507 a tenant cannot fulfil it by publishing a procedure. The route out
  // is the substantive one — clear the registry, or complete the checklist —
  // which is achievable and IS the control. Nobody is stranded; they have to
  // do the work.
  //
  // WHY THE EMPTY CASE STAYS A PASS, where the vendor checkers above turned
  // theirs into `null`. The two absences are not the same absence.
  // `isDataProcessor` is a checkbox seeded `false`, so "none flagged" is the
  // state of a question NOBODY ANSWERED. An employee roster with nobody
  // `departed` and no leave process is the tenant's own affirmative statement
  // that nobody has left — entered once, read everywhere, and the same record
  // every other employee-derived verdict trusts. Returning `null` here would
  // also leave an obligation with no document route permanently unfulfillable
  // for a tenant that has genuinely never had a leaver, which fails a
  // different way.
  //
  // What that argument does NOT cover is a roster nobody maintains — a tenant
  // that offboards outside Tryggr and never marks anyone departed. That is a
  // staleness problem in the roster itself, shared by every employee-derived
  // verdict in the product, and it is not fixed by making this one checker
  // refuse to answer.
  'employees-assets-returned': (state) => {
    const employees = state.employees || [];
    const hasLeaveProcess = (e) => (e.jmlProcesses || []).some((p) => p.type === 'leave');
    const leavers = leaverPopulation(employees);
    if (leavers.length === 0) {
      return {
        pass: true,
        summary:
          employees.length === 0
            ? 'No employees on record (N/A)'
            : `None of the ${employees.length} employee(s) on the roster is leaving or has departed (N/A)`,
        details: { employees: employees.length, leaverCount: 0 },
      };
    }
    const withProcess = leavers.filter(hasLeaveProcess);
    const withoutProcess = leavers.filter((e) => !hasLeaveProcess(e));
    const unreturned = withProcess.filter((e) => {
      const leaveProcess = (e.jmlProcesses || []).find((p) => p.type === 'leave');
      const item = (leaveProcess?.checklist || []).find((c) => c.key === 'asset-return');
      return !item || !item.completed;
    });
    const unevidenced = [...withoutProcess, ...unreturned];
    const parts = [];
    if (withoutProcess.length) parts.push(`${withoutProcess.length} have no leaver record`);
    if (unreturned.length) parts.push(`${unreturned.length} have not returned their assets`);
    return {
      pass: unevidenced.length === 0,
      summary:
        unevidenced.length === 0
          ? `All ${leavers.length} leaver(s)/departed employee(s) have completed asset return`
          : `${unevidenced.length} of ${leavers.length} leaver(s)/departed employee(s) cannot evidence asset return — ${parts.join(', ')}`,
      details: {
        leaverCount: leavers.length,
        withoutLeaverRecord: withoutProcess.length,
        unreturnedCount: unreturned.length,
        unevidencedNames: unevidenced.map((e) => e.name || e.id),
      },
    };
  },

  'employees-evaluated': (state) => {
    // GH #6654 — `status !== 'departed'`, NOT `status === 'active'`.
    // The remedy this PR pushes a tenant toward is EmployeesPage's bulk
    // "Start Joiner", which sets `status: 'onboarding'` on everyone selected.
    // While this filtered on `active`, following the product's own advice
    // SHRANK this denominator and moved this verdict favourably — a fix whose
    // remediation path quietly improves a different figure. An onboarding
    // employee has joined and is in scope; only a departed one is not.
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    const criteria = state.employeeEvaluationCriteria || [];
    if (employees.length === 0 || criteria.length === 0) {
      return { pass: true, summary: 'No active employees or criteria defined (N/A)', details: {} };
    }
    const unevaluated = employees.filter((e) => {
      const evals = e.evaluations || {};
      return Object.keys(evals).length === 0;
    });
    return {
      pass: unevaluated.length === 0,
      summary:
        unevaluated.length === 0
          ? `All ${employees.length} active employee(s) have evaluation records`
          : `${unevaluated.length} employee(s) have no evaluation records`,
      details: { unevaluatedNames: unevaluated.map((e) => e.name || e.id) },
    };
  },

  // Reads state.evidenceRequirements + state.evidenceRecords. Pass when every
  // active employee in the requirement target has a verified, in-date record.
  //
  // ITS TWO EMPTY-POPULATION BRANCHES RETURN `pass: null`, NOT `true` (GH
  // #6457). Every obligation that uses this checker — obl-security-awareness,
  // obl-nda-signed, obl-employee-screening — is `strategy: 'any'`, so whatever
  // it returns satisfies that obligation on its own. `evidenceRequirements` is
  // seeded EMPTY (`initialState.js`) and a requirement is an admin-authored
  // Settings record, so "no requirement of this type is defined" is the DEFAULT
  // state of every tenant: a vacuous `true` there fulfilled obl-security-
  // awareness on every tenant that had never opened that Settings screen,
  // whatever its training said. This checker makes a POSITIVE claim — evidence
  // of a named type has been collected and verified — and an undefined
  // requirement is the absence of any evidence either way, which §5's fail-safe
  // bias resolves to the restrictive answer.
  //
  // The one knowable exception is a tenant with no active employees: nothing
  // can be collected from nobody, and `null` there would manufacture a
  // permanent failure rather than fail safe. Same narrowing as
  // `employees-training` and `training-deadline-overdue`'s archive branch.
  'evidence-requirement-met': (state, _standardTask, condition) => {
    const evidenceType = condition?.evidenceType;
    if (!evidenceType) {
      return {
        pass: false,
        summary: 'evidence-requirement-met: missing evidenceType in condition',
        details: {},
      };
    }
    const requirements = (state.evidenceRequirements || []).filter(
      (r) => r.active !== false && r.evidenceType === evidenceType
    );
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    if (requirements.length === 0) {
      if (employees.length === 0) {
        return {
          pass: true,
          summary: `No active employees, so no '${evidenceType}' evidence to collect (N/A)`,
          details: {},
        };
      }
      return {
        pass: null,
        summary: `No active '${evidenceType}' evidence requirement is defined — this evidence cannot be shown to have been collected`,
        details: { activeEmployees: employees.length },
      };
    }
    const records = state.evidenceRecords || [];
    const now = Date.now();

    const matchesTarget = (emp, target) => {
      if (!target || target.type === 'all') return true;
      if (target.type === 'role') {
        // GH #6269 (EMP-B-002): an unassigned-but-acknowledged entry is
        // preserved with `unassignedAt` rather than removed — it must not
        // still match a role-based automation target.
        const roles = (emp.assignedRoles || emp.roles || []).filter(
          (r) => typeof r === 'string' || !r.unassignedAt
        );
        return roles.some(
          (r) =>
            (typeof r === 'string' ? r : r.id) === target.value ||
            (typeof r !== 'string' && r.name === target.value)
        );
      }
      if (target.type === 'department') return emp.department === target.value;
      if (target.type === 'team') return emp.team === target.value;
      if (target.type === 'employment-type') return emp.employmentType === target.value;
      if (target.type === 'specific') return emp.id === target.value;
      return false;
    };

    const missingByReq = [];
    // GH #6661 — a recurring requirement whose interval cannot be read (a
    // `custom` frequency with no usable `frequencyDays`) is a trap, and the
    // summary has to say so or it names a remedy that cannot work. Such a
    // requirement can never be satisfied: `calcValidUntil` returns null when it
    // verifies a record, the sweeper refuses to derive a date it cannot
    // justify, and this checker refuses to count an unstamped record — so a
    // tenant can collect the evidence again forever and the number never moves.
    // The fix is one field in Settings, and saying which requirement it is
    // turns an unfixable-looking gap into a one-click one.
    const undatableReqs = [];
    let totalExpected = 0;
    let totalSatisfied = 0;

    for (const req of requirements) {
      const targetEmployees = employees.filter((e) => matchesTarget(e, req.target));
      if (targetEmployees.length === 0) continue;
      totalExpected += targetEmployees.length;

      // GH #6661 — an absent `validUntil` means two opposite things, and this
      // read them as one. For a one-time requirement (an on-hire NDA, an ID
      // check) no expiry is HONEST and the record is current forever. For a
      // recurring one it is a MISSING FIELD, and reading it as "never expires"
      // turns a recurring governance activity into a one-off box tick — §12's
      // named failure, monitoring is not an event. The sweeper backfills a
      // derived expiry for recurring requirements so these records converge;
      // until it has run, refusing to count them is the fail-safe direction.
      const recurs = requirementRecurs(req);
      if (recurs && recurrenceDays(req) === null) undatableReqs.push(req.name || req.id);

      const missing = [];
      for (const emp of targetEmployees) {
        const empRecords = records.filter(
          (r) => r.requirementId === req.id && r.employeeId === emp.id
        );
        const verified = empRecords.find(
          (r) =>
            r.status === 'verified' &&
            (r.validUntil ? new Date(r.validUntil).getTime() > now : !recurs)
        );
        if (verified) totalSatisfied++;
        else missing.push(emp.name || emp.id);
      }
      if (missing.length > 0)
        missingByReq.push({ requirementId: req.id, requirementName: req.name, missing });
    }

    if (totalExpected === 0) {
      if (employees.length === 0) {
        return {
          pass: true,
          summary: `No active employees, so no '${evidenceType}' evidence to collect (N/A)`,
          details: {},
        };
      }
      return {
        pass: null,
        summary: `'${evidenceType}' requirement(s) defined but no active employee matches their target — this evidence cannot be shown to have been collected`,
        details: { activeEmployees: employees.length, requirements: requirements.length },
      };
    }
    const pass = missingByReq.length === 0;
    const undatableNote = undatableReqs.length
      ? ` — ${undatableReqs.length === 1 ? 'requirement' : 'requirements'} ${undatableReqs
          .map((n) => `'${n}'`)
          .join(
            ', '
          )} recur${undatableReqs.length === 1 ? 's' : ''} on a custom interval that is not set, so evidence against ${undatableReqs.length === 1 ? 'it' : 'them'} cannot be dated; set the interval in Settings → Evidence Requirements`
      : '';
    return {
      pass,
      summary: pass
        ? `All ${totalSatisfied} employee(s) have verified, in-date '${evidenceType}' evidence`
        : `${totalExpected - totalSatisfied}/${totalExpected} employees missing/expired '${evidenceType}' evidence${undatableNote}`,
      details: {
        totalExpected,
        totalSatisfied,
        missingByReq,
        undatableRequirements: undatableReqs,
      },
    };
  },

  // Reads state.assets and state.employees. Pass when no asset in the central
  // registry is still assigned to a leaver (active leave JML) or a departed
  // employee. Complements employees-assets-returned, which only inspects the
  // leaver JML checklist key. Either path proves SCF-AST-003.
  'assets-returned-by-leavers': (state) => {
    const employees = state.employees || [];
    const assets = state.assets || [];
    const leaverIds = new Set(leaverPopulation(employees).map((e) => e.id));
    if (leaverIds.size === 0) {
      return { pass: true, summary: 'No leavers or departed employees (N/A)', details: {} };
    }
    const outstanding = assets.filter(
      (a) => a.status !== 'retired' && leaverIds.has(a.assigneeId) && !a.returnedAt
    );
    return {
      pass: outstanding.length === 0,
      summary:
        outstanding.length === 0
          ? `All ${leaverIds.size} leaver(s)/departed employee(s) have no outstanding registry assets`
          : `${outstanding.length} asset(s) still assigned to leavers: ${outstanding.map((a) => a.name || a.id).join(', ')}`,
      details: { leaverCount: leaverIds.size, outstandingAssetIds: outstanding.map((a) => a.id) },
    };
  },

  // Reads state.roleDefinitions and state.employees. Pass when every active
  // employee with assigned roles has acknowledged the current responsibilities
  // version of every role assigned to them. Fails closed (not N/A) when no
  // role definitions exist, or when definitions exist but no employee has
  // been assigned one — a tenant that has not adopted the roles framework,
  // or has defined roles without assigning them, cannot be evidenced as
  // having acknowledged roles and responsibilities.
  'roles-acknowledged': (state) => {
    const roleDefinitions = state.roleDefinitions || [];
    // GH #6654 — `status !== 'departed'`, NOT `status === 'active'`.
    // The remedy this PR pushes a tenant toward is EmployeesPage's bulk
    // "Start Joiner", which sets `status: 'onboarding'` on everyone selected.
    // While this filtered on `active`, following the product's own advice
    // SHRANK this denominator and moved this verdict favourably — a fix whose
    // remediation path quietly improves a different figure. An onboarding
    // employee has joined and is in scope; only a departed one is not.
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    if (roleDefinitions.length === 0) {
      return {
        pass: false,
        summary: 'No roles defined yet — role acknowledgement cannot be evidenced',
        details: {},
      };
    }
    const roleVersionById = new Map(
      roleDefinitions.map((r) => [r.id, r.responsibilitiesVersion ?? r.version ?? 1])
    );
    // GH #6269 (EMP-B-002): an unassigned-but-acknowledged entry is preserved
    // with `unassignedAt` rather than removed — it is evidence, not a current
    // assignment, and must not count as one here.
    const employeesWithRoles = employees.filter((e) =>
      (e.assignedRoles || []).some((r) => !r?.unassignedAt)
    );
    if (employeesWithRoles.length === 0) {
      return {
        pass: false,
        summary: 'Roles defined but not yet assigned to any employee',
        details: {},
      };
    }
    const pending = [];
    for (const emp of employeesWithRoles) {
      const acks = emp.roleAcknowledgements || [];
      for (const roleRef of (emp.assignedRoles || []).filter((r) => !r?.unassignedAt)) {
        const roleId = typeof roleRef === 'string' ? roleRef : roleRef.id;
        if (!roleVersionById.has(roleId)) continue;
        const requiredVersion = roleVersionById.get(roleId);
        const acknowledged = acks.some(
          (a) => a.roleId === roleId && (a.version ?? 1) === requiredVersion
        );
        if (!acknowledged) pending.push({ employee: emp.name || emp.id, roleId });
      }
    }
    return {
      pass: pending.length === 0,
      summary:
        pending.length === 0
          ? `All ${employeesWithRoles.length} employee(s) have acknowledged the current version of every assigned role`
          : `${pending.length} role acknowledgement(s) pending across ${new Set(pending.map((p) => p.employee)).size} employee(s)`,
      details: { pending },
    };
  },

  // Reads state.roleDefinitions, state.employees, state.evidenceRecords.
  // NIS2 Art. 20 — the management body must oversee cybersecurity AND have
  // completed cybersecurity training. Pass when at least one role is flagged
  // managementBody:true, every active employee in that role has the joiner
  // 'security-training' checklist item completed, and (if a
  // 'training-attestation' evidence requirement exists) holds verified, in-date
  // evidence. Fails (not N/A) when no role is flagged managementBody — NIS2
  // cannot be vacuously satisfied; tenants out of NIS2 scope must remove the
  // obligation via framework membership, not via a vacuous pass.
  'management-body-trained': (state) => {
    const roleDefinitions = state.roleDefinitions || [];
    const mbRoles = roleDefinitions.filter((r) => r.managementBody === true);
    if (mbRoles.length === 0) {
      return {
        pass: false,
        summary:
          'No role flagged as management body — NIS2 Art. 20 requires a designated management body',
        details: { hint: 'Flag the relevant role definition with managementBody:true' },
      };
    }
    const mbRoleIds = new Set(mbRoles.map((r) => r.id));
    // GH #6654 — `status !== 'departed'`, NOT `status === 'active'`.
    // The remedy this PR pushes a tenant toward is EmployeesPage's bulk
    // "Start Joiner", which sets `status: 'onboarding'` on everyone selected.
    // While this filtered on `active`, following the product's own advice
    // SHRANK this denominator and moved this verdict favourably — a fix whose
    // remediation path quietly improves a different figure. An onboarding
    // employee has joined and is in scope; only a departed one is not.
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    const mbEmployees = employees.filter((e) =>
      (e.assignedRoles || []).some((roleRef) => {
        if (roleRef && typeof roleRef !== 'string' && roleRef.unassignedAt) return false;
        const id = typeof roleRef === 'string' ? roleRef : roleRef.id;
        return mbRoleIds.has(id);
      })
    );
    if (mbEmployees.length === 0) {
      return {
        pass: false,
        summary: 'No active employee is assigned to a management-body role',
        details: { managementBodyRoles: mbRoles.map((r) => r.name || r.id) },
      };
    }
    const untrained = mbEmployees.filter((emp) => {
      const joinProcess = (emp.jmlProcesses || []).find((p) => p.type === 'join');
      const checklist = joinProcess?.checklist || [];
      const trainingItem = checklist.find((c) => c.key === 'security-training');
      return !trainingItem || !trainingItem.completed;
    });

    // Optional: if a training-attestation requirement exists, also require an
    // in-date verified record per management-body member.
    const trainingReqs = (state.evidenceRequirements || []).filter(
      (r) => r.active !== false && r.evidenceType === 'training-attestation'
    );
    const now = Date.now();
    const missingAttestation = [];
    if (trainingReqs.length > 0) {
      for (const emp of mbEmployees) {
        const empRecords = (state.evidenceRecords || []).filter((r) => r.employeeId === emp.id);
        // GH #6661 — see `evidence-requirement-met` above. The requirement is
        // resolved per record rather than tested with `.some`, because whether
        // an unstamped record still counts depends on which requirement backs
        // it, not on whether any training requirement exists.
        const verified = empRecords.find((r) => {
          const tr = trainingReqs.find((t) => t.id === r.requirementId);
          if (!tr || r.status !== 'verified') return false;
          return r.validUntil ? new Date(r.validUntil).getTime() > now : !requirementRecurs(tr);
        });
        if (!verified) missingAttestation.push(emp.name || emp.id);
      }
    }

    const pass = untrained.length === 0 && missingAttestation.length === 0;
    return {
      pass,
      summary: pass
        ? `All ${mbEmployees.length} management-body member(s) have completed cybersecurity training` +
          (trainingReqs.length > 0 ? ' and hold a verified, in-date training attestation' : '')
        : [
            untrained.length > 0
              ? `${untrained.length} management-body member(s) without training-complete: ${untrained.map((e) => e.name || e.id).join(', ')}`
              : '',
            missingAttestation.length > 0
              ? `${missingAttestation.length} without verified training attestation: ${missingAttestation.join(', ')}`
              : '',
          ]
            .filter(Boolean)
            .join('; '),
      details: {
        managementBodyMembers: mbEmployees.map((e) => e.name || e.id),
        untrained,
        missingAttestation,
      },
    };
  },

  // Reads state.employees + state.securityPosture.staleAccounts.thresholdDays.
  // Pass when no Entra-synced active employee has a sign-in older than the
  // configured threshold. Fails closed when no employee is Entra-synced — the
  // tenant has not connected Entra, so staleness cannot be evidenced.
  'entra-stale-accounts-clear': (state) => {
    const employees = state.employees || [];
    const entraActive = employees.filter(
      // GH #6654 — `!== 'departed'`, matching `privileged-mfa-enrolled` below.
      // Excluding `moving` and `offboarding` from a STALENESS check can only
      // ever make a tenant look better, and a person on their way out whose
      // Entra account is still enabled and unused for 90 days is the finding,
      // not the exclusion. Adding statuses here can only make this stricter.
      (e) => e.syncSource === 'entra-id' && e.status !== 'departed'
    );
    if (entraActive.length === 0) {
      return {
        pass: false,
        summary:
          'No Entra-synced employees in scope — stale-account status cannot be evidenced yet',
        details: {},
      };
    }
    const thresholdDays = state.securityPosture?.staleAccounts?.thresholdDays || 90;
    const cutoff = Date.now() - thresholdDays * 24 * 60 * 60 * 1000;
    const stale = entraActive.filter((e) => {
      if (!e.lastSignInAt) return false;
      return new Date(e.lastSignInAt).getTime() <= cutoff;
    });
    return {
      pass: stale.length === 0,
      summary:
        stale.length === 0
          ? `All ${entraActive.length} Entra-synced employee(s) in scope have signed in within ${thresholdDays} days`
          : `${stale.length} Entra-synced employee(s) stale (no sign-in for ${thresholdDays}+ days): ${stale.map((e) => e.name || e.id).join(', ')}`,
      details: { thresholdDays, staleNames: stale.map((e) => e.name || e.id) },
    };
  },

  // Reads state.employees. Pass when every Entra-synced employee with at least
  // one privileged admin role has MFA tracked-and-enabled. Fails closed when no
  // privileged Entra accounts exist — including when Entra is not connected at
  // all — since that is not evidence that privileged MFA is enforced.
  'privileged-mfa-enrolled': (state) => {
    const employees = state.employees || [];
    const privileged = employees.filter(
      (e) =>
        e.syncSource === 'entra-id' &&
        Array.isArray(e.entraAdminRoles) &&
        e.entraAdminRoles.length > 0 &&
        e.status !== 'departed'
    );
    if (privileged.length === 0) {
      return {
        pass: false,
        summary: 'No privileged Entra accounts found — MFA enrolment cannot be evidenced yet',
        details: {},
      };
    }
    const noMfa = privileged.filter((e) => e.mfaEnabled !== true);
    return {
      pass: noMfa.length === 0,
      summary:
        noMfa.length === 0
          ? `All ${privileged.length} privileged Entra account(s) have MFA enabled`
          : `${noMfa.length}/${privileged.length} privileged Entra account(s) without MFA: ${noMfa.map((e) => e.name || e.id).join(', ')}`,
      details: { totalPrivileged: privileged.length, withoutMfa: noMfa.map((e) => e.name || e.id) },
    };
  },

  // ── Asset & system-based ───────────────────────────────────────────────

  'asset-inventory-exists': (state) => {
    // Check centralised asset registry first, fall back to employee assets for backward compat
    const registryAssets = (state.assets || []).length;
    const employeeAssets = (state.employees || []).reduce(
      (sum, e) => sum + (e.assets || []).length,
      0
    );
    const totalAssets = registryAssets + employeeAssets;
    return {
      pass: totalAssets > 0,
      summary:
        totalAssets > 0
          ? `Asset inventory contains ${totalAssets} asset(s)` +
            (registryAssets > 0
              ? ` (${registryAssets} in registry` +
                (employeeAssets > 0 ? `, ${employeeAssets} on employees)` : ')')
              : ` (${employeeAssets} on employees)`)
          : 'No assets recorded in asset registry or employee records',
      details: { totalAssets, registryAssets, employeeAssets },
    };
  },

  // Fails closed (not N/A) when the registry is empty — a tenant with zero
  // assets recorded cannot be evidenced as having assigned owners to them.
  'asset-owners-assigned': (state) => {
    const assets = state.assets || [];
    if (assets.length === 0) {
      return {
        pass: false,
        summary: 'No assets in registry — asset ownership cannot be evidenced',
        details: {},
      };
    }
    const unowned = assets.filter((a) => !a.ownerId && a.status === 'active');
    return {
      pass: unowned.length === 0,
      summary:
        unowned.length === 0
          ? `All ${assets.filter((a) => a.status === 'active').length} active asset(s) have assigned owners`
          : `${unowned.length} active asset(s) have no owner assigned`,
      details: { unownedNames: unowned.map((a) => a.name || a.id) },
    };
  },

  'asset-classification-complete': (state) => {
    const assets = state.assets || [];
    if (assets.length === 0) {
      return { pass: true, summary: 'No assets in registry (N/A)', details: {} };
    }
    const active = assets.filter((a) => a.status === 'active');
    const unclassified = active.filter((a) => !a.dataClassification);
    return {
      pass: unclassified.length === 0,
      summary:
        unclassified.length === 0
          ? `All ${active.length} active asset(s) have data classifications`
          : `${unclassified.length} active asset(s) have no data classification`,
      details: { unclassifiedNames: unclassified.map((a) => a.name || a.id) },
    };
  },

  'activity-log-configured': (state) => {
    const settings = state.activityLogSettings || {};
    const configured = settings.retentionDays && settings.retentionDays > 0;
    return {
      pass: configured,
      summary: configured
        ? `Activity log retention configured: ${settings.retentionDays} days`
        : 'Activity log retention not configured',
      details: { retentionDays: settings.retentionDays },
    };
  },

  'jml-process-complete': (state, standardTask, condition) => {
    const jmlType = condition?.jmlType || 'join';
    const employees = state.employees || [];
    const withProcess = employees.filter((e) =>
      (e.jmlProcesses || []).some((p) => p.type === jmlType)
    );
    if (withProcess.length === 0) {
      return {
        pass: false,
        summary: `No ${jmlType} processes recorded yet — cannot be evidenced`,
        details: {},
      };
    }
    const incomplete = withProcess.filter((e) => {
      const process = (e.jmlProcesses || []).find((p) => p.type === jmlType);
      return process && process.status !== 'completed';
    });
    return {
      pass: incomplete.length === 0,
      summary:
        incomplete.length === 0
          ? `All ${withProcess.length} ${jmlType} process(es) are completed`
          : `${incomplete.length} ${jmlType} process(es) are not yet completed`,
      details: { incompleteNames: incomplete.map((e) => e.name || e.id) },
    };
  },

  // ── GDPR privacy module–based ──────────────────────────────────────────

  // GDPR Art. 30 — at least one active entry in the RoPA register
  'ropa-entries-exist': (state) => {
    const active = (state.ropaEntries || []).filter((e) => e.status === 'active');
    return {
      pass: active.length > 0,
      summary:
        active.length > 0
          ? `RoPA register contains ${active.length} active processing activit${active.length === 1 ? 'y' : 'ies'}`
          : 'No active entries in the Records of Processing Activities register',
      details: { activeCount: active.length },
    };
  },

  // GDPR Art. 6 / 9 — all active RoPA entries have a documented lawful basis
  // GH #6428 — this and the four privacy checkers below returned `pass: true`
  // on absent data, and each is the FIRST criterion of a `strategy: 'any'`
  // obligation, so an empty register did not merely skip a check: it
  // SATISFIED the obligation. A tenant that had simply not started its RoPA
  // was told GDPR Art. 6 was met.
  //
  // The summaries were the sharpest part of the problem. "No high-risk
  // processing activities identified" asserts a finding the product never
  // made — it saw no data, which is not the same thing, and
  // `nodeClassification.js` then presented that as OBSERVED/HIGH-confidence
  // evidence in the assurance case.
  //
  // Every one of the five obligations also carries `document-published` /
  // `document-approved` / `document-within-sla` criteria, so failing the data
  // checker closed CANNOT make an obligation permanently unmet: a tenant that
  // publishes and approves its procedure still fulfils it. That is what makes
  // `null` safe here rather than a manufactured permanent failure.
  //
  // Art. 30 requires a record of processing activities from any organisation
  // processing personal data. An empty register is an unstarted control, not
  // a satisfied one.
  'ropa-lawful-basis-complete': (state) => {
    const active = (state.ropaEntries || []).filter((e) => e.status === 'active');
    if (active.length === 0) {
      return {
        pass: null,
        summary:
          'No active RoPA entries — lawful basis cannot be evidenced (Art. 30 requires a record of processing activities)',
        details: { activeRopaEntries: 0, notRecorded: true },
      };
    }
    const missing = active.filter((e) => !e.lawfulBasis);
    return {
      pass: missing.length === 0,
      summary:
        missing.length === 0
          ? `All ${active.length} active processing activit${active.length === 1 ? 'y' : 'ies'} have a documented lawful basis`
          : `${missing.length} processing activit${missing.length === 1 ? 'y' : 'ies'} missing lawful basis: ${missing.map((e) => e.name || e.id).join(', ')}`,
      details: {
        total: active.length,
        missingCount: missing.length,
        missingNames: missing.map((e) => e.name || e.id),
      },
    };
  },

  // GDPR Art. 44–49 — all international transfers in the RoPA have a documented mechanism
  // GH #6428. The first draft of this fix kept a vacuous pass here on an
  // argued knowability split — "the RoPA is populated and declares no
  // transfers, so we know there are none". THE FORM SAYS OTHERWISE, and the
  // co-owner review caught it: `ROPAFormModal.jsx` puts `status` on tab 0,
  // third-country transfers on tab 2 of 4, enables Save from any tab as soon
  // as `name` is non-empty, and offers no "no third-country transfers"
  // affirmation anywhere. So an ACTIVE entry with `thirdCountryTransfers: []`
  // records that nobody opened the tab — not that there are none.
  //
  // An empty sub-record is not an answered question. Absence of a declaration
  // is not a declaration of absence (§10, 2026-08-15: safety comes from an
  // affirmative record, never from the absence of a challenge), so every
  // branch that cannot distinguish "asked and answered none" from "never
  // asked" returns null. Restoring a pass here requires the form to force the
  // answer first — tracked on #6658.
  'ropa-transfers-documented': (state) => {
    const { active, transfers, undocumented, unanswered } = ropaTransferPosition(state);
    const names = (list) => list.map((e) => e.name || e.id).join(', ');
    if (active.length === 0) {
      return {
        pass: null,
        summary:
          'No active RoPA entries — whether any international transfers exist has not been recorded, so transfer mechanisms cannot be evidenced',
        details: { activeRopaEntries: 0, notRecorded: true },
      };
    }
    const withTransfers = active.length - unanswered.length;
    if (undocumented.length > 0) {
      return {
        pass: false,
        summary: `${undocumented.length} processing activit${undocumented.length === 1 ? 'y' : 'ies'} with undocumented transfer mechanisms: ${names(undocumented)}`,
        details: { withTransfers, undocumentedCount: undocumented.length },
      };
    }
    if (unanswered.length > 0) {
      return {
        pass: null,
        summary: `${unanswered.length} of the ${active.length} active processing activit${active.length === 1 ? 'y records' : 'ies record'} no international transfer, but the register never asks the question — so no transfer position is evidenced: ${names(unanswered)}`,
        details: { total: active.length, withTransfers, notRecorded: true },
      };
    }
    return {
      pass: true,
      summary: `All ${withTransfers} processing activit${withTransfers === 1 ? 'y' : 'ies'} with international transfers have documented transfer mechanisms (${transfers.length} transfer(s))`,
      details: { withTransfers, undocumentedCount: 0 },
    };
  },

  // GDPR Art. 35 — all registered DPIAs are approved.
  // GH #6428: the empty branch used to read "no high-risk processing
  // activities identified", which states a conclusion of an assessment that
  // was never performed. Nothing in the product derives DPIA necessity from
  // the RoPA, so an empty register means the question is unanswered, not
  // answered "no".
  'dpia-approved-exists': (state) => {
    const dpias = state.dpias || [];
    if (dpias.length === 0) {
      return {
        pass: null,
        summary:
          'No DPIAs registered — whether any processing requires an assessment has not been determined',
        details: { registeredDpias: 0, notRecorded: true },
      };
    }
    const pending = dpias.filter((d) => !['approved', 'rejected'].includes(d.status));
    const approved = dpias.filter((d) => d.status === 'approved');
    const pass = pending.length === 0 && approved.length > 0;
    return {
      pass,
      summary: pass
        ? `${approved.length} DPIA${approved.length === 1 ? '' : 's'} approved; all assessments complete`
        : pending.length > 0
          ? `${pending.length} DPIA${pending.length === 1 ? '' : 's'} still in progress: ${pending.map((d) => d.name || d.id).join(', ')}`
          : 'No DPIAs have been approved',
      details: { total: dpias.length, approved: approved.length, pending: pending.length },
    };
  },

  // GDPR Art. 37 — a DPO has been formally designated (name + appointment date recorded)
  'dpo-designated': (state) => {
    const dpo = state.dpoDesignation || {};
    const designated = !!(dpo.name && dpo.appointmentDate);
    return {
      pass: designated,
      summary: designated
        ? `DPO designated: ${dpo.name} (appointed ${dpo.appointmentDate})`
        : dpo.name
          ? 'DPO named but appointment date not recorded'
          : 'No DPO designation has been recorded',
      details: { name: dpo.name || null, appointmentDate: dpo.appointmentDate || null },
    };
  },

  // GDPR Art. 7 — consent purposes are defined in the Consent Register.
  //
  // GH #6428, and this was the worst of the five: BOTH branches returned
  // `pass: true`. There was no code path that returned false, so
  // `obl-consent` — Art. 7 — was auto-fulfilled for every tenant in the
  // product, whatever their consent register contained. Unlike the other
  // four this was never about plan gating; it fired everywhere, always.
  //
  // The populated branch now carries a real predicate, AT RECORD GRAIN.
  //
  // Art. 7(1) is "the controller shall be able to DEMONSTRATE that the data
  // subject has consented". A register listing purposes with no consent
  // records is exactly the case where it cannot — so counting the records and
  // then not using them (the first draft of this fix) left the same vacuous
  // truth one grain down: purposes defined, zero subjects, Art. 7 fulfilled.
  //
  // The evidence Art. 7 wants is on the RECORD, not the purpose:
  // `RecordFormModal` collects `privacyNoticeVersion` per consent record
  // alongside `givenDate` and `method`. So a purpose is evidenced when it has
  // at least one active record AND those records name the notice relied on.
  // That enforces the register's own schema rather than setting policy here.
  //
  // NOT filtered by `lawfulBasis`: `ConsentRegister.jsx` is the only writer of
  // `consentPurposes` in the repo and its purpose form has no lawful-basis
  // control — it hardcodes `'consent'`. Filtering on it would describe a
  // discrimination the product cannot make, and pin a branch no tenant data
  // can reach.
  'consent-purposes-defined': (state) => {
    const purposes = state.consentPurposes || [];
    if (purposes.length === 0) {
      return {
        pass: null,
        summary:
          'No consent purposes registered — whether consent-based processing is in use has not been recorded',
        details: { purposeCount: 0, notRecorded: true },
      };
    }
    const activeRecords = (state.consentRecords || []).filter((r) => r.status === 'active');
    const byPurpose = (p) => activeRecords.filter((r) => r.purposeId === p.id);
    const unevidenced = purposes.filter((p) => {
      const records = byPurpose(p);
      return records.length === 0 || records.some((r) => !r.privacyNoticeVersion);
    });
    return {
      pass: unevidenced.length === 0,
      summary:
        unevidenced.length === 0
          ? `All ${purposes.length} consent purpose${purposes.length === 1 ? '' : 's'} carry active consent records naming the privacy notice relied on (${activeRecords.length} record${activeRecords.length === 1 ? '' : 's'})`
          : `${unevidenced.length} consent purpose${unevidenced.length === 1 ? '' : 's'} cannot demonstrate the consent relied on — no active record, or records naming no privacy notice version: ${unevidenced.map((p) => p.name || p.id).join(', ')}`,
      details: {
        purposeCount: purposes.length,
        unevidencedCount: unevidenced.length,
        activeRecordCount: activeRecords.length,
      },
    };
  },

  // GDPR Art. 15–22 — no open DSR is overdue past its 30-day (or 90-day extended) deadline
  // GH #6428. "No requests recorded, therefore none overdue" is true of this
  // checker in isolation, but the obligation it fulfils on its own
  // (`obl-data-subject-rights`, `strategy: 'any'`) is about having a WORKING
  // Art. 15–22 process — and an empty request log evidences nothing about
  // that. The document criteria on the same obligation are what a tenant with
  // genuinely zero requests uses to fulfil it, which is the honest route.
  'dsr-no-overdue-requests': (state) => {
    const requests = state.dsrRequests || [];
    if (requests.length === 0) {
      return {
        pass: null,
        summary:
          'No data subject requests recorded — the request-handling process has never been exercised, so its timeliness cannot be evidenced',
        details: { recordedRequests: 0, notRecorded: true },
      };
    }
    const now = Date.now();
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
    const overdue = requests.filter((r) => {
      if (['completed', 'refused'].includes(r.status)) return false;
      const received = r.receivedDate ? new Date(r.receivedDate).getTime() : null;
      if (!received) return false;
      const limit = r.isExtended ? NINETY_DAYS_MS : THIRTY_DAYS_MS;
      return now - received >= limit;
    });
    return {
      pass: overdue.length === 0,
      summary:
        overdue.length === 0
          ? `All ${requests.length} data subject request${requests.length === 1 ? '' : 's'} are within response deadlines`
          : `${overdue.length} data subject request${overdue.length === 1 ? '' : 's'} overdue past the ${overdue.some((r) => r.isExtended) ? '90' : '30'}-day response deadline`,
      details: {
        total: requests.length,
        overdueCount: overdue.length,
        overdueIds: overdue.map((r) => r.id),
      },
    };
  },

  // ── Vulnerability management — auto-evaluation against Defender feed ────
  // Boundary VUL-B-005: these checkers read state.msVulnerabilities (the
  // synced-by-system feed) and state.vulnerabilities (the derived
  // projection). They never recalculate criticality and never write back.
  // Boundary VUL-B-001: read-only against the raw feed.

  'defender-feed-fresh': (state, _standardTask, condition = {}) => {
    const feed = state.msVulnerabilities;
    const maxAgeDays = Number.isFinite(condition.maxAgeDays) ? condition.maxAgeDays : 90;
    if (!feed?.syncedAt) {
      return {
        pass: false,
        summary:
          'No Microsoft Defender feed has been synced yet — connect the integration to satisfy this obligation automatically.',
        details: { feedConnected: false },
      };
    }
    const ageMs = Date.now() - new Date(feed.syncedAt).getTime();
    const maxMs = maxAgeDays * 86400000;
    const ageDays = Math.floor(ageMs / 86400000);
    return {
      pass: ageMs <= maxMs,
      summary:
        ageMs <= maxMs
          ? `Defender feed last synced ${ageDays}d ago — within the ${maxAgeDays}d freshness window`
          : `Defender feed last synced ${ageDays}d ago — exceeds the ${maxAgeDays}d freshness threshold`,
      details: {
        syncedAt: feed.syncedAt,
        ageDays,
        maxAgeDays,
        cveCount: feed.cves?.length || 0,
      },
    };
  },

  // Hardened per SDEB-05: a tenant with zero tracked vulnerabilities is
  // structurally indistinguishable from a genuinely clean backlog unless we
  // also check that a feed has actually been connected and synced. We reuse
  // the same signal as 'defender-feed-fresh' above (state.msVulnerabilities
  // .syncedAt) rather than inventing a new state field — VUL-B-001 already
  // establishes state.msVulnerabilities as the system-synced source of truth
  // for whether Microsoft Defender has ever reported in for this tenant.
  'vulnerability-priority-controlled': (state, _standardTask, condition = {}) => {
    const feedConnected = Boolean(state.msVulnerabilities?.syncedAt);
    // Rows the feed no longer reports (VUL-B-011) are history, not the backlog.
    const vulns = (Array.isArray(state.vulnerabilities) ? state.vulnerabilities : []).filter(
      (v) => !v || v.baseStatus !== 'not-reported'
    );
    const maxOpenP1 = Number.isFinite(condition.maxOpenP1) ? condition.maxOpenP1 : 0;
    const maxOpenP2 = Number.isFinite(condition.maxOpenP2) ? condition.maxOpenP2 : 5;

    if (!feedConnected) {
      return {
        pass: false,
        summary:
          'No vulnerability feed has ever been connected — the patch backlog cannot be evidenced yet. Connect Microsoft Defender for Endpoint (Settings → Integrations) so open P1/P2 findings can be counted automatically.',
        details: {
          feedConnected: false,
          openP1: 0,
          openP2: 0,
          maxOpenP1,
          maxOpenP2,
          totalOpen: 0,
          totalCorrelated: vulns.length,
        },
      };
    }

    const open = vulns.filter((v) => v.effectiveStatus === 'open');
    const openP1 = open.filter((v) => v.tier === 'P1').length;
    const openP2 = open.filter((v) => v.tier === 'P2').length;
    const breach = openP1 > maxOpenP1 || openP2 > maxOpenP2;
    // A CVE list the sync recorded as cut off or failed (GH #7181) cannot show
    // the backlog is within budget; it can still show it is over.
    const cveCoverage = state.msVulnerabilities?.coverage?.cves;
    if (!breach && (cveCoverage === 'capped' || cveCoverage === 'failed')) {
      return {
        pass: false,
        summary:
          cveCoverage === 'capped'
            ? 'The Defender CVE list filled its 500-row page, so it may be incomplete — the patch backlog cannot be shown to be within budget.'
            : 'The last Defender CVE read failed — the patch backlog cannot be shown to be within budget.',
        details: { feedConnected: true, feedCoverage: cveCoverage, openP1, openP2, maxOpenP1, maxOpenP2, totalOpen: open.length, totalCorrelated: vulns.length },
      };
    }
    return {
      pass: !breach,
      summary: breach
        ? `Patch backlog over budget: ${openP1} open P1 (max ${maxOpenP1}), ${openP2} open P2 (max ${maxOpenP2})`
        : `Patch backlog within budget: ${openP1} open P1, ${openP2} open P2 (limits ${maxOpenP1}/${maxOpenP2})`,
      details: {
        openP1,
        openP2,
        maxOpenP1,
        maxOpenP2,
        totalOpen: open.length,
        totalCorrelated: vulns.length,
        feedConnected: true,
      },
    };
  },

  // Reads state.trainingModules / state.trainingCourses, state.trainingPrograms,
  // state.employees, state.evidenceRecords.
  // Pass when every active non-departed employee who is assigned to at least
  // one active training course — directly OR through a live training programme
  // that contains it — holds a verified, non-expired 'training-completion'
  // evidence record for each of their assigned courses.
  // Evidence records are created server-side (evtc-<courseId>-<employeeId>)
  // when the employee passes the quiz in employee-portal-update.
  //
  // THIS CHECKER SETS A COMPLIANCE VERDICT. It is a fulfilment criterion of
  // obl-security-awareness, which is Gate 5 input, and that obligation's
  // strategy is 'any' — so this returning `true` satisfies the whole obligation
  // on its own. Two consequences are load-bearing and deliberate:
  //
  //   1. It resolves assignment through `isModuleAssignedToEmployee`, the same
  //      derivation the employee portal and the admin UI use. It used to walk
  //      `course.assignments` only, so a tenant that assigns its security
  //      awareness training exclusively through programmes — the pattern the
  //      product's own Programs section steers an organisation toward — produced
  //      zero pairs and read satisfied against training it had never looked at.
  //      GH #6457; TRYGGR_INTENT.md §6.2's second named failure, live.
  //   2. Neither empty-population branch returns `pass: true` any more. "No
  //      training modules exist" and "modules exist but nobody is assigned" are
  //      not evidence that security awareness training is completed; they are
  //      the absence of any evidence either way, and §5's fail-safe bias says an
  //      evaluator that cannot answer returns the restrictive answer, not the
  //      reassuring one. `pass: null` (unknown) rather than `pass: false`,
  //      because asserting a failure we have not observed is its own dishonesty
  //      — and null is falsy, so every consumer (evaluateAutoEvidence's
  //      passCount, obligationEvaluator's allPass, gapAutoSignal) already treats
  //      it as not-satisfied. The obligation's other three criteria — a training
  //      attestation, the JML security-training checkbox, an acknowledged
  //      awareness document — remain able to satisfy it on a tenant that
  //      evidences its awareness training some other way.
  //
  //      THAT LAST SENTENCE IS A BOUNDARY CITATION AND HAD TO BE EARNED ACROSS
  //      ALL FOUR TERMS, because `obl-security-awareness` is `strategy: 'any'`:
  //      any one term returning a vacuous `true` fulfils the obligation on its
  //      own, so hardening one term while a sibling still passes vacuously
  //      moves no tenant's verdict at all. It took FOUR rounds to enumerate
  //      them, and every round's citation was wrong until the next one checked
  //      it — including the round-3 citation that stood here and called term 4
  //      "already strict". The enumeration, re-derived at round 4, with the
  //      branch that decides each one named rather than summarised:
  //        1. training-completion-recorded — hardened. No active modules, or
  //           modules with no assigned pair → `null`.
  //        2. evidence-requirement-met ('training-attestation') — hardened;
  //           its "no requirement defined" branch was the DEFAULT state of
  //           every tenant, since `evidenceRequirements` seeds empty. → `null`,
  //           except with no active employees.
  //        3. employees-training — hardened; "no joiner records" is the
  //           default on any tenant that has not run the JML joiner flow.
  //           → `null`, except with no active employees.
  //        4. document-acknowledged — NOT "already strict", which is what the
  //           three previous rounds asserted. It returns `false` only for the
  //           empty-DOCUMENT population; the empty-READER population — one or
  //           more published documents that resolve nobody, which is what
  //           `readerRequirement: { mode: 'none' }` (the creation default)
  //           produces — returned a vacuous `true` and outvoted all three
  //           hardened terms. Hardened at round 4 → `null`, except with no
  //           active employees.
  //      Two rules this list has now broken four times running: do not add a
  //      fifth term without checking what it returns on EVERY empty population
  //      it has — not just the obvious one — and do not cite this list without
  //      re-deriving it against the checkers themselves.
  'training-completion-recorded': (state) => {
    const courses = activeTrainingModulesOf(state);
    if (courses.length === 0) {
      return {
        pass: null,
        summary: 'No active training modules defined — training completion cannot be evidenced',
        details: {},
      };
    }

    const programs = trainingProgramsOf(state);
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    const records = state.evidenceRecords || [];
    const now = Date.now();

    const missing = [];
    let totalPairs = 0;
    let satisfiedPairs = 0;

    for (const emp of employees) {
      for (const course of courses) {
        if (!isModuleAssignedToEmployee(course, emp, programs)) continue;
        totalPairs++;
        if (currentTrainingEvidence(course, emp, records, now)) {
          satisfiedPairs++;
        } else {
          missing.push({ employee: emp.name || emp.id, courseTitle: course.title });
        }
      }
    }

    if (totalPairs === 0) {
      return {
        pass: null,
        summary:
          'Active training modules exist but no active employee is assigned to any, directly or through a programme — training completion cannot be evidenced',
        details: {},
      };
    }

    return {
      pass: missing.length === 0,
      summary:
        missing.length === 0
          ? `All ${satisfiedPairs} assigned employee-course pair(s) have verified, in-date completion records`
          : `${missing.length}/${totalPairs} assigned employee-course pair(s) missing verified completion records`,
      details: { totalPairs, satisfiedPairs, missing },
    };
  },

  // Reads state.trainingModules / state.trainingCourses, state.trainingPrograms,
  // state.employees, state.evidenceRecords.
  // Pass when no active training assignment has a PAST deadline without a
  // verified, in-date completion for the assigned employee.
  // Vacuous-pass when no active modules exist, or when every assignment either
  // has a future/missing deadline or has already been completed. That vacuous
  // pass is correct for this checker and deliberately kept: it is a NEGATIVE
  // claim — "nothing has lapsed" — which is true of a tenant with no deadlines,
  // unlike training-completion-recorded's positive claim above.
  // This is the deadline-specific counterpart of training-completion-recorded:
  // it only fires on missed deadlines, not on in-progress (not-yet-due) work.
  //
  // TWO DEFECTS FIXED HERE, BOTH GH #6455. It read `state.trainingCourses`
  // alone — the legacy key — so on any tenant provisioned since the rename to
  // `state.trainingModules` the list was empty and it returned "No active
  // training courses defined (N/A)" unconditionally: it had never once been
  // capable of reporting an overdue training deadline. And its assignment and
  // deadline resolution were both direct-only, so a deadline set on a programme
  // assignment was invisible to it. Its first real run on a current-shape
  // tenant is therefore a behaviour change even though the key fix reads like a
  // typo repair.
  'training-deadline-overdue': (state) => {
    const allModules = trainingModulesOf(state);
    const courses = allModules.filter((c) => c && !c.archivedAt);
    if (courses.length === 0) {
      // "Nothing has lapsed" is a claim about a population. When that population
      // has been WITHDRAWN rather than never existed, the claim is not knowable
      // — and reporting it as true is a favourable gate movement produced by a
      // shrinking denominator, which is the over-claim vector this checker's
      // sibling was just hardened against. Archiving is one unconfirmed click
      // (`TrainingTab.jsx`), and this obligation is `strategy: 'all'` with this
      // as its only criterion, so a `true` here takes Gate 5 for SCF-PPL-003
      // from failing to passing with nothing shown anywhere.
      if (allModules.length > 0) {
        return {
          pass: null,
          summary:
            'Every training module on this tenant is archived — whether a deadline lapsed before they were withdrawn cannot be determined',
          details: { archivedModules: allModules.length },
        };
      }
      // Genuinely no training has ever existed here. Nothing could have lapsed,
      // and that IS knowable, so the vacuous pass is honest rather than
      // reassuring. Kept deliberately: `null` here would leave every tenant
      // that does not use the training module permanently unable to satisfy
      // obl-training-deadline-compliance, which is a manufactured failure, not
      // a fail-safe one.
      return { pass: true, summary: 'No training modules defined (N/A)', details: {} };
    }

    const programs = trainingProgramsOf(state);
    const employees = (state.employees || []).filter((e) => e.status !== 'departed');
    const records = state.evidenceRecords || [];
    const nowMs = Date.now();

    // WITHDRAWN DEADLINES. The all-archived guard above catches the rarest form
    // of the shrinking denominator; this catches the common one. Archiving the
    // single module that carries a lapsed deadline — or the programme that
    // carries it — leaves other modules active, so `courses.length > 0`, no
    // active pair yields a past deadline, and the checker would report the
    // reassuring "no assignments with past deadlines (N/A)". One unconfirmed
    // click (`TrainingTab.jsx`'s toggleArchive: no confirm, no activity log)
    // would take Gate 5 for SCF-PPL-003 from unmet to satisfied, on a tenant
    // where a deadline demonstrably lapsed. Whether it lapsed before withdrawal
    // is not knowable from state, so it resolves to unknown, never to true.
    const isPast = (d) => {
      const t = new Date(d).getTime();
      return Number.isFinite(t) && t <= nowMs;
    };
    // Counted per WITHDRAWN DEADLINE, not per employee-deadline pair: one
    // archived module on a 200-person tenant is one withdrawal to investigate,
    // not 200. And each one names the module, because the only way to resolve
    // this result is the criteria-waiver path — a justification written against
    // a bare number is written blind.
    const withdrawn = new Map();
    for (const emp of employees) {
      for (const course of allModules) {
        // An archived module withdraws every deadline on it, live programme or
        // not; a live module withdraws only those carried by archived programmes.
        const live = course?.archivedAt ? [] : deadlinesForEmployee(course, emp, programs);
        const all = deadlinesForEmployee(course, emp, programs, {
          includeArchivedPrograms: true,
        });
        const liveCounts = new Map();
        for (const d of live) liveCounts.set(d, (liveCounts.get(d) ?? 0) + 1);
        for (const d of all) {
          const stillLive = liveCounts.get(d) ?? 0;
          if (stillLive > 0) {
            liveCounts.set(d, stillLive - 1);
            continue;
          }
          if (!isPast(d)) continue;
          const key = `${course?.id}|${d}`;
          if (!withdrawn.has(key)) {
            withdrawn.set(key, {
              courseTitle: course?.title || course?.id,
              deadline: d,
              moduleArchived: !!course?.archivedAt,
              affectedEmployees: 0,
            });
          }
          withdrawn.get(key).affectedEmployees += 1;
        }
      }
    }
    const withdrawnItems = [...withdrawn.values()];

    let hasDeadlineAssignments = false;
    const overdueItems = [];

    for (const emp of employees) {
      for (const course of courses) {
        if (!isModuleAssignedToEmployee(course, emp, programs)) continue;
        const pastDeadline = earliestPastDeadlineForEmployee(course, emp, programs, nowMs);
        if (!pastDeadline) continue;
        hasDeadlineAssignments = true;

        if (!currentTrainingEvidence(course, emp, records, nowMs)) {
          overdueItems.push({
            employee: emp.name || emp.id,
            courseTitle: course.title,
            deadline: pastDeadline,
          });
        }
      }
    }

    // A withdrawn past deadline makes the reassuring answer unknowable, whether
    // it is the only deadline on the tenant or sits alongside clean live ones.
    // It never softens a failure: `overdueItems` already observed is reported.
    if (overdueItems.length === 0 && withdrawnItems.length > 0) {
      return {
        pass: null,
        summary: `${withdrawnItems.length} training deadline(s) that had already passed were withdrawn by archiving — whether they lapsed before withdrawal cannot be determined`,
        details: { withdrawnDeadlines: withdrawnItems.length, withdrawnItems, overdueItems: [] },
      };
    }

    if (!hasDeadlineAssignments) {
      return {
        pass: true,
        summary: 'No training assignments with past deadlines (N/A)',
        details: {},
      };
    }

    return {
      pass: overdueItems.length === 0,
      summary:
        overdueItems.length === 0
          ? 'All deadline-bearing training assignments completed on time'
          : `${overdueItems.length} training assignment(s) past deadline without a passing completion`,
      details: { overdueItems },
    };
  },

  // Reads state.uarCampaigns and state.evidenceRecords.
  // Pass when at least one UAR campaign has been closed and has a verified,
  // non-expired uar-report evidence record. Fails closed when no campaigns
  // have ever been run — a tenant that has never used the UAR module has not
  // evidenced a user access review.
  'uar-campaign-completed': (state) => {
    const campaigns = state.uarCampaigns || [];
    if (campaigns.length === 0) {
      return {
        pass: false,
        summary: 'No UAR campaigns have been run yet — a user access review cannot be evidenced',
        details: {},
      };
    }

    const now = Date.now();
    const records = state.evidenceRecords || [];
    const closedWithEvidence = campaigns.filter((c) => {
      if (c.status !== 'closed' || !c.evidenceId) return false;
      // GH #6661. A user access review is inherently recurring:
      // the close path stamps `validUntil` at 90 days, so there is no such
      // thing as a UAR record that legitimately never expires. Reading an
      // absent expiry as current reported the control evidenced indefinitely
      // from a campaign closed before that stamping shipped, or imported — a
      // 2023 access review answering for twelve missed cycles.
      //
      // NOT observed in any tenant: the 2026-09-14 run of
      // `migrations/diagnostics/unstamped-evidence-expiry-count.sql` found no
      // closed UAR campaign in production at all, and both dev/test campaigns
      // already carried a stamp. This closes the class before it had a victim.
      // Do not cite it as a live over-claim.
      //
      // Unlike the two requirement-backed checkers there is no `frequency` to
      // consult, so an unstamped record is simply not current. The sweeper
      // backfills these from the campaign's own `closedAt`, which is what makes
      // the stale ones expire rather than merely stop counting.
      const rec = records.find(
        (r) =>
          r.id === c.evidenceId &&
          r.status === 'verified' &&
          !!r.validUntil &&
          new Date(r.validUntil).getTime() > now
      );
      return !!rec;
    });

    if (closedWithEvidence.length > 0) {
      const latest = closedWithEvidence.reduce((a, b) =>
        (a.closedAt || '') > (b.closedAt || '') ? a : b
      );
      return {
        pass: true,
        summary: `${closedWithEvidence.length} UAR campaign(s) closed with verified evidence (latest: ${latest.title})`,
        details: { count: closedWithEvidence.length },
      };
    }

    const closedCount = campaigns.filter((c) => c.status === 'closed').length;
    const activeCount = campaigns.filter((c) => c.status === 'active').length;
    return {
      pass: false,
      summary:
        closedCount > 0
          ? `${closedCount} closed campaign(s) but evidence records are expired or missing — close a campaign to generate a new report`
          : `No UAR campaigns have been closed yet (${activeCount} active)`,
      details: { closedCount, activeCount },
    };
  },

  // ── Governance-based ────────────────────────────────────────────────────

  // ISO 27001:2022 Cl. 9.3; SCF-MSS-006 — top management review of the ISMS.
  // Reads state.managementReviews (logged via the Management Review module,
  // ManagementReviewPage.jsx / ManagementReviewModal.jsx). A review record
  // only counts as evidence once status === 'complete' — the modal itself
  // gates that transition on conductedDate being set (a 'scheduled' or
  // 'in-progress' record is not proof the meeting actually happened) — and
  // only while conductedDate is within the obligation's own review cycle.
  // Passes when at least one completed review's conductedDate falls inside
  // that window; a published minutes document remains an independent,
  // additive path to fulfilment (this obligation's strategy is 'any').
  'management-review-conducted': (state) => {
    const CADENCE_MS = 182 * 24 * 60 * 60 * 1000; // matches obl-management-review's cadence.frequency ('6m')
    const now = Date.now();
    const completed = (state.managementReviews || []).filter(
      (r) => r.status === 'complete' && r.conductedDate
    );
    const current = completed
      .filter((r) => now - new Date(r.conductedDate).getTime() <= CADENCE_MS)
      .sort((a, b) => new Date(b.conductedDate).getTime() - new Date(a.conductedDate).getTime())[0];

    return {
      pass: !!current,
      summary: current
        ? `Management review "${current.title || current.id}" conducted ${current.conductedDate}`
        : completed.length > 0
          ? 'Most recent completed management review is outside the current review cycle'
          : 'No completed management review meeting has been recorded',
      details: {
        reviewId: current?.id || null,
        conductedDate: current?.conductedDate || null,
        completedReviewCount: completed.length,
      },
    };
  },

  // ── Integration-verified obligations ─────────────────────────────────────
  // Reads a derived audit object written by a server-side integration handler
  // (e.g. state.githubAudit from the GitHub PR audit) and reports whether a
  // named boolean check passed. Obligations with strategy:'integration' carry
  // fulfilmentCriteria of { type:'integration-check', integrationKey, check },
  // which obligationEvaluator.toAutoEvidenceFormat bridges into this checker.
  // OBL-B-002: the pass/fail is derived from the audit, never set manually.
  'integration-check': (state, _standardTask, condition = {}) => {
    const AUDIT_BY_KEY = {
      'github-pr-audit': state.githubAudit,
    };
    const audit = AUDIT_BY_KEY[condition.integrationKey];
    if (!audit || !audit.checks) {
      return {
        pass: false,
        summary: `No ${condition.integrationKey || 'integration'} audit data yet — connect the integration to verify this automatically.`,
        details: { connected: false },
      };
    }

    const pass = audit.checks[condition.check] === true;
    const s = audit.summary || {};
    let summary;
    if (condition.check === 'allMergesHavePeerReview') {
      summary = pass
        ? `GitHub PR audit: all ${s.totalPrsMerged ?? 0} PR(s) merged to development in the last ${audit.windowDays ?? 90} days have peer review (last checked ${audit.lastSyncAt})`
        : `GitHub PR audit: ${s.prsWithoutPeerReview ?? 0} of ${s.totalPrsMerged ?? 0} merged PR(s) lack an approving review from someone other than the author (last checked ${audit.lastSyncAt})`;
    } else if (condition.check === 'criticalPrsHaveTestFiles') {
      summary = pass
        ? `GitHub PR audit: ${s.criticalHighPrsWithTestFile ?? 0} of ${s.criticalHighPrs ?? 0} Critical/High SDLC risk PR(s) include a regression test file (last checked ${audit.lastSyncAt})`
        : `GitHub PR audit: ${s.criticalHighPrsWithoutTestFile ?? 0} Critical/High SDLC risk PR(s) are missing a regression test file (last checked ${audit.lastSyncAt})`;
    } else {
      summary = `Integration check '${condition.check}': ${pass ? 'passed' : 'not met'} (last checked ${audit.lastSyncAt})`;
    }

    return {
      pass,
      summary,
      details: { lastSyncAt: audit.lastSyncAt, partialSync: !!audit.partialSync, ...s },
    };
  },

  // ── Manual-strategy residual confirmation ────────────────────────────────
  // A document proves a policy/procedure exists and is current — it never
  // proves the underlying activity (pentest, backup test, review) actually
  // happened. This checker looks for a human attestation record instead of
  // deriving pass/fail from anything document-shaped. OBL-B-002: still
  // evaluated live from state each call — remove the record and this reverts.
  'manual-attestation': (state, standardTask) => {
    const attestations = (state.obligationAttestations || []).filter(
      (a) => a.obligationId === standardTask.obligationId
    );
    if (attestations.length === 0) {
      return {
        pass: false,
        summary:
          'Awaiting confirmation that this activity actually took place — a published document alone does not satisfy this criterion',
        details: {},
      };
    }
    const latest = [...attestations].sort(
      (a, b) => new Date(b.attestedAt).getTime() - new Date(a.attestedAt).getTime()
    )[0];

    // GH #6705, founder decision A — AN ATTESTATION EXPIRES.
    //
    // This returned `pass: true` for the latest attestation at ANY date. It
    // compared `attestedAt` to nothing. So an obligation declaring
    // `cadence: { frequency: '1y' }`, whose own criterion text reads "confirm
    // the annual review has been done", was satisfied permanently by one click
    // — §12's named failure, verbatim: a "done" state that one closed task
    // satisfies for good is a governance gap wearing the clothes of a finished
    // record.
    //
    // Two other mechanisms hid it. `computeDueDate` is `enabledAt + cadence`,
    // a date fixed at enablement rather than rolling; and
    // `evaluateManualObligation` returns fulfilled BEFORE `isPastDue` is
    // consulted, so once every criterion passed the obligation could never go
    // overdue and never came back. Neither matters once the attestation itself
    // goes stale: `allPass` turns false, the obligation reopens, and the loop
    // gets back round to PLAN.
    //
    // THE CYCLE IS THE STRICTER OF THE TWO, never whichever the tenant set.
    //
    // `computeDueDate` resolves `cadenceOverride?.frequency || cadence.frequency`
    // and lets the override win outright. Copying that here would have handed
    // the tenant an exemption: `frequency: 'once'` yields no interval, so a
    // `cadenceOverride: { frequency: 'once' }` on an ANNUAL obligation would
    // mean its attestation never expires — exactly the permanent pass this
    // change exists to remove, reintroduced one field over.
    //
    // That is not hypothetical. `obligationMigration.js` writes
    // `cadenceOverride` from a legacy `adoptedStandardTasks[].frequency`
    // whenever it differs from the definition, and `standardTasks.js` offered
    // `'once'` in its frequency picker. Production data can carry it.
    //
    // Note the asymmetry it would have created: waiving a criterion demands a
    // written justification and renders a visible "waived" chip, while a
    // cadence override of `'once'` would achieve more — permanently fulfilled,
    // no expiry, no marker on any surface — with no justification and no
    // author. A shorter override is honoured, because a tenant may always hold
    // itself to a tighter cycle than the definition asks.
    //
    // Only a definition that genuinely does not recur exempts the attestation.
    const enabled = (state.enabledObligations || []).find(
      (eo) => eo.obligationId === standardTask.obligationId
    );
    const def = OBLIGATIONS_BY_ID.get(standardTask.obligationId);
    const defFrequency = def?.cadence?.frequency;
    const overrideFrequency = enabled?.cadenceOverride?.frequency;
    const defMs = defFrequency ? cadenceToMs(defFrequency) : null;
    const overrideMs = overrideFrequency ? cadenceToMs(overrideFrequency) : null;
    // defMs === null means the DEFINITION does not recur ('once') — or the
    // obligation is not in the catalogue, in which case there is no cadence to
    // judge against and no honest expiry to apply.
    const cycleMs =
      defMs === null ? null : overrideMs === null ? defMs : Math.min(defMs, overrideMs);
    const frequency =
      cycleMs === overrideMs && overrideFrequency ? overrideFrequency : defFrequency;
    const attestedMs = new Date(latest.attestedAt).getTime();
    const expiresAt =
      cycleMs && Number.isFinite(attestedMs) ? new Date(attestedMs + cycleMs).toISOString() : null;
    const stale = expiresAt !== null && new Date(expiresAt).getTime() <= Date.now();

    const who = latest.attestedByName || 'a user';
    const when = latest.attestedAt;
    // The summary is the only place this reaches the customer: `fulfilmentState`
    // carries type/pass/summary and drops `details`, so the cycle has to be
    // named in the sentence, not left in a field nobody renders.
    const cycleLabel =
      { '1m': 'monthly', '3m': 'quarterly', '6m': 'six-monthly', '1y': 'annual' }[frequency] ||
      frequency;
    return {
      pass: !stale,
      summary: stale
        ? `Last confirmed by ${who} on ${when}, which is more than one ${cycleLabel} cycle ago — this activity needs confirming again`
        : `Confirmed by ${who} on ${when}${latest.note ? ` — ${latest.note}` : ''}`,
      details: {
        attestationId: latest.id,
        attestedAt: latest.attestedAt,
        attestedBy: latest.attestedBy,
        cadence: frequency || null,
        expiresAt,
      },
    };
  },
};

// ── Plan entitlement: the third verdict (GH #6608, #6627) ────────────────────
//
// Every checker above answers a question about tenant data. None of them can
// tell tenant data that is ABSENT from tenant data that was WITHHELD — the two
// arrive as the same empty array. PLAN-B-001's read-side filter
// (azure-functions/shared/state/planStateFilter.js) empties a gated module's
// keys before the response leaves the Function App, and the browser's copy of
// the state IS the response: there is no second source these checkers could
// consult.
//
// Left alone, that turns a withholding into a compliance claim. Roughly a third
// of the checkers above treat an empty population as "nothing to check (N/A)"
// and return pass:true, and the obligations consuming them are strategy:'any',
// where autoEvidence's own canAutoComplete is `passCount > 0` — so ONE vacuous
// pass fulfils the whole obligation. #6627 is that failure live in production
// today, from the `assets` gate that shipped with the original five:
// 'assets-returned-by-leavers' builds its leaver set from the UNGATED employee
// roster and filters the GATED asset registry, so a free-plan tenant with
// departed staff still holding equipment is told obl-asset-return /
// SCF-AST-003 is fulfilled. TRYGGR_INTENT.md §6's second named failure.
//
// So the engine gets a THIRD verdict, distinct from both pass and fail:
//
//   pass: null, notEntitled: true
//
// `pass: null` is this file's existing vocabulary for "the checker could not
// determine an answer" — already falsy everywhere a verdict is counted, and
// already rendered as a neutral '?' rather than a red ✗ by ObligationCatalog,
// ObligationTaskDetail and GapAnalysisDetail. That is exactly what is wanted
// here: never a tick, and never a failure the tenant could be asked to fix,
// because the thing they would have to fix is a module their plan does not
// include. `notEntitled` carries the reason so those surfaces can say so.
//
// Deliberately NOT `pass: false`: a hard fail is a claim that the control is
// unmet, which this checker never established, and it would put remediation
// work in front of a tenant who cannot do it (#6385).
//
// The verdict is decided at DISPATCH, before the checker runs, rather than
// inside each empty-population branch. Patching branches would mean auditing
// which internal path of each checker can be moved favourably by missing data
// and re-auditing it on every change — the discipline that the original five
// map entries were written before, and that #6627 is the cost of. A checker
// whose declared inputs were withheld simply does not run, which is fail-safe
// by construction and independent of what its branches do.
//
// The declaration below is what makes that possible, so it is exhaustive over
// CHECKERS and asserted to be by autoEvidence.planGating.test.js. A checker
// with NO entry is treated as not assessable whenever ANYTHING was withheld
// from the response — an undeclared checker must never be the quiet way past
// this gate. A checker that genuinely reads no tenant state declares `[]`.
//
// Keys are top-level state keys, including ones reached through a helper:
// resolveReaders() reads `employees`, trainingModulesOf()/trainingProgramsOf()
// read `trainingModules` / `trainingCourses` / `trainingPrograms`.
const CHECKER_STATE_DEPENDENCIES = {
  'document-published': ['documents'],
  'document-approved': ['documents'],
  // resolveReaders(doc, employees): an emptied roster resolves no readers, the
  // per-document loop `continue`s on every document, and `allAcked` stays true.
  // GH #6457 round 4: that same mechanism was reachable WITHOUT plan gating —
  // `readerRequirement: { mode: 'none' }` is the creation default, so an
  // ordinary tenant produced it too. This note was the only place the mechanism
  // was written down, filed under plan gating, and it never propagated to the
  // checker three hundred lines above; the checker now returns `null` for it.
  // A safety fact recorded against one mechanism does not cover another.
  // documentAssignments: the legacy list folded into every document's readers
  // until RDRA-04's migration has run (DOC-B-026, design §d.1) — withholding it
  // would drop those readers from the population without saying so.
  'document-acknowledged': ['documents', 'employees', 'documentAssignments'],
  'document-within-sla': ['documents'],
  'continuity-exercise-evidence': ['documents', 'bcdrDrTests'],
  // Both, deliberately: the review due date resolves across the runbook's own
  // field AND its linked document's nextReview, so withholding documents would
  // otherwise change which date governs without saying so (#6608's shape).
  'runbook-review-current': ['bcdrRunbooks', 'documents', 'bcdrDrTests'],
  'internal-audit-programme-complete': ['internalAudits', 'auditProgrammes'],
  'risk-register-exists': ['risks'],
  'risks-reviewed': ['risks'],
  'risks-approved': ['risks'],
  'vendor-assessed': ['vendors'],
  'vendor-assessment-current': ['vendors'],
  'vendor-contracts-tracked': ['vendors'],
  'subprocessor-registry-maintained': ['vendors'],
  'subprocessor-dpa-signed': ['vendors'],
  'subprocessor-oversight-current': ['vendors'],
  'transfer-basis-documented': ['vendors', 'ropaEntries'],
  'employees-screened': ['employees'],
  'employees-nda-signed': ['employees'],
  'employees-training': ['employees'],
  'employees-access-revoked': ['employees'],
  'employees-assets-returned': ['employees'],
  'employees-evaluated': ['employees', 'employeeEvaluationCriteria'],
  'evidence-requirement-met': ['evidenceRequirements', 'employees', 'evidenceRecords'],
  'assets-returned-by-leavers': ['employees', 'assets'],
  'roles-acknowledged': ['roleDefinitions', 'employees'],
  'management-body-trained': [
    'roleDefinitions',
    'employees',
    'evidenceRequirements',
    'evidenceRecords',
  ],
  'entra-stale-accounts-clear': ['employees', 'securityPosture'],
  'privileged-mfa-enrolled': ['employees'],
  'asset-inventory-exists': ['assets', 'employees'],
  'asset-owners-assigned': ['assets'],
  'asset-classification-complete': ['assets'],
  'activity-log-configured': ['activityLogSettings'],
  'jml-process-complete': ['employees'],
  'ropa-entries-exist': ['ropaEntries'],
  'ropa-lawful-basis-complete': ['ropaEntries'],
  'ropa-transfers-documented': ['ropaEntries'],
  'dpia-approved-exists': ['dpias'],
  'dpo-designated': ['dpoDesignation'],
  'consent-purposes-defined': ['consentPurposes', 'consentRecords'],
  'dsr-no-overdue-requests': ['dsrRequests'],
  'defender-feed-fresh': ['msVulnerabilities'],
  'vulnerability-priority-controlled': ['msVulnerabilities', 'vulnerabilities'],
  'training-completion-recorded': [
    'trainingModules',
    'trainingCourses',
    'trainingPrograms',
    'employees',
    'evidenceRecords',
  ],
  'training-deadline-overdue': [
    'trainingModules',
    'trainingCourses',
    'trainingPrograms',
    'employees',
    'evidenceRecords',
  ],
  'uar-campaign-completed': ['uarCampaigns', 'evidenceRecords'],
  'management-review-conducted': ['managementReviews'],
  'integration-check': ['githubAudit'],
  'manual-attestation': ['obligationAttestations', 'enabledObligations'],
};

/**
 * The not-entitled verdict. Shaped so that anything reading only `pass` sees a
 * falsy value and can never count it as fulfilment.
 */
function notEntitledResult(withheld) {
  const modules = withheldModuleNames(withheld);
  const label = moduleLabelList(modules);
  return {
    pass: null,
    notEntitled: true,
    summary: `Not assessable — ${label} ${modules.length > 1 ? 'are' : 'is'} not included in this tenant's plan, so the data this criterion checks was not provided`,
    details: {
      planWithheldModules: modules,
      planWithheldKeys: withheld.map((w) => w.key),
    },
  };
}

/**
 * Which of a checker's declared inputs this response withheld, if any.
 *
 * Returns [] whenever the response carried no plan-gate marker at all — a
 * state assembled in a test, restored from a cache, or served by a Function
 * App older than #6608 evaluates exactly as it did before. This mechanism can
 * only ever ADD not-entitled verdicts.
 */
function planWithheldForChecker(conditionType, state) {
  const declared = Object.prototype.hasOwnProperty.call(CHECKER_STATE_DEPENDENCIES, conditionType)
    ? CHECKER_STATE_DEPENDENCIES[conditionType]
    : null;
  if (declared) return planWithheldAmong(state, declared);
  // Undeclared checker: fail closed against everything this response withheld,
  // rather than let a new checker slip past the gate unaudited. Costs a fully
  // entitled tenant nothing — their marker withholds no keys.
  return planWithheldAmong(state, Object.keys(planWithheldKeyMap(state)));
}

// ── Main evaluation function ─────────────────────────────────────────────

/**
 * Evaluate all auto-evidence conditions for a standard task against
 * the current tenant state.
 *
 * @param {object} standardTask - Standard task template from STANDARD_TASKS
 * @param {object} state        - Full tenant state (documents, tasks, employees, etc.)
 * @returns {object} {
 *   canAutoComplete: boolean,     — true if all/any conditions met (per strategy)
 *   results: Array<{ type, pass, summary, details }>,
 *   evidenceSnapshot: string,     — human-readable evidence summary for task closure
 *   checkedAt: string,            — ISO timestamp of evaluation
 * }
 */
function evaluateAutoEvidence(standardTask, rawState) {
  // Every checker below reads people from this state: sample people and
  // seeded evidence never count (GH #7870).
  const state = withoutSampleRecords(rawState);
  if (!standardTask.autoEvidence) {
    return {
      canAutoComplete: false,
      results: [],
      evidenceSnapshot: 'This task requires manual evidence collection.',
      checkedAt: new Date().toISOString(),
    };
  }

  const { strategy, conditions } = standardTask.autoEvidence;
  const results = [];

  for (const condition of conditions) {
    const checker = CHECKERS[condition.type];
    if (!checker) {
      results.push({
        type: condition.type,
        pass: false,
        summary: `Unknown condition type: ${condition.type}`,
        details: {},
      });
      continue;
    }

    // #6608 — entitlement is decided BEFORE the checker runs. A checker whose
    // inputs this response withheld cannot answer honestly about them, and the
    // one answer it must never give is the reassuring one.
    const withheld = planWithheldForChecker(condition.type, state);
    if (withheld.length > 0) {
      results.push({
        type: condition.type,
        description: condition.description,
        ...notEntitledResult(withheld),
      });
      continue;
    }

    const result = checker(state, standardTask, condition);
    results.push({
      type: condition.type,
      description: condition.description,
      ...result,
    });
  }

  const passCount = results.filter((r) => r.pass).length;
  const anyNotEntitled = results.some((r) => r.notEntitled);
  // A task must not auto-close on partial evidence when part of what it claims
  // was never shown to the engine. For strategy 'any' this is the load-bearing
  // half: `passCount > 0` would otherwise let one genuinely-passing criterion
  // carry a task whose other criteria were withheld.
  const canAutoComplete =
    !anyNotEntitled && (strategy === 'all' ? passCount === results.length : passCount > 0); // strategy === 'any'

  // Build evidence snapshot
  // A null is "could not determine", not an observed failure. The snapshot is
  // attached to the completed task as audit evidence, so rendering unknown as a
  // hard ✗ would assert something the checker never established.
  const lines = results.map(
    (r) => `${r.pass ? '✓' : r.pass == null ? '?' : '✗'} ${r.description || r.type}: ${r.summary}`
  );

  const evidenceSnapshot = [
    `Auto-evidence evaluation — ${canAutoComplete ? 'PASSED' : anyNotEntitled ? 'NOT ASSESSABLE' : 'NOT MET'}`,
    `Strategy: ${strategy} (${passCount}/${results.length} conditions met)`,
    '',
    ...lines,
  ].join('\n');

  return {
    canAutoComplete,
    results,
    notEntitled: anyNotEntitled,
    notEntitledModules: anyNotEntitled
      ? withheldModuleNames(
          results
            .filter((r) => r.notEntitled)
            .flatMap((r) =>
              (r.details?.planWithheldModules || []).map((m) => ({ key: null, module: m }))
            )
        )
      : [],
    evidenceSnapshot,
    checkedAt: new Date().toISOString(),
  };
}

/**
 * Quick check: can a specific standard task auto-complete right now?
 */
function canAutoComplete(standardTask, state) {
  return evaluateAutoEvidence(standardTask, state).canAutoComplete;
}

/**
 * Evaluate all adopted standard tasks and return a summary.
 * Useful for dashboard / overview displays.
 */
function evaluateAllStandardTasks(adoptedStandardTasks, standardTaskCatalog, rawState) {
  const state = withoutSampleRecords(rawState);
  const results = [];

  for (const adopted of adoptedStandardTasks) {
    const template = standardTaskCatalog.find((t) => t.id === adopted.standardTaskId);
    if (!template) continue;

    const evaluation = evaluateAutoEvidence(template, state);
    results.push({
      standardTaskId: adopted.standardTaskId,
      title: template.title,
      category: template.category,
      frequency: template.frequency,
      canAutoComplete: evaluation.canAutoComplete,
      passCount: evaluation.results.filter((r) => r.pass).length,
      totalConditions: evaluation.results.length,
      hasAutoEvidence: !!template.autoEvidence,
    });
  }

  return results;
}

module.exports = {
  CHECKERS,
  CHECKER_STATE_DEPENDENCIES,
  // The document-to-obligation link, shared with the CTX-06 base measure
  // (measurement/measures/governance.cjs, GH #7127) so the scope document it
  // reads is the one the obligation checkers read.
  getControlIdsForTask,
  docsForControls,
  planWithheldForChecker,
  evaluateAutoEvidence,
  canAutoComplete,
  evaluateAllStandardTasks,
};
